/**
 * Single source of truth for ALL React-app configuration.
 *
 * The user's brief explicitly says:
 *     "only one file should be there for app configuration"
 *
 * So everything that another part of the app needs to know about — API
 * endpoints, route paths, the navigation menu, status colours, validation
 * limits, branding, storage keys, default page sizes, etc. — lives here.
 *
 * If you need to tweak any URL, label, colour or default value, do it in
 * THIS file and nowhere else.
 *
 * Values that legitimately differ between environments (dev / staging /
 * production) are read from `import.meta.env.VITE_*` so they can be
 * overridden via `.env.local` without code changes.
 */

import { apiOriginFromLocation } from '@taskydo/web-core/apiOrigin.js';
import { navForProduct } from '@taskydo/web-core/paths.js';
import { getConfig } from '@taskydo/web-core/runtime.js';

// ---------------------------------------------------------------------------
// 1. Branding
// ---------------------------------------------------------------------------
export const APP = {
  name: import.meta.env.VITE_APP_NAME || 'Taskydo',
  productKey: 'taskydo',
  tagline: 'Tasks & Complaints, simplified',
  logoUrl: '/static/images/logo-icon.png', // served by Django in production
};

// ---------------------------------------------------------------------------
// 1b. Multi-tenant host context
// ---------------------------------------------------------------------------
// Mirrors the backend ``TASKYDO_BASE_DOMAIN`` setting (see
// ``taskydo/settings.py``).  The frontend uses this *only* to choose
// which UI to render — public/super-admin shell vs. tenant business
// shell.  All security is still enforced server-side via:
//
//   * ``django_tenants`` schema isolation (the public schema does not
//     even contain the tenant business tables),
//   * ``TenantAwareJWTAuthentication`` (rejects tokens whose
//     ``schema_name`` claim doesn't match the request's resolved
//     tenant),
//   * ``TenantUserBindingMiddleware`` (rejects authenticated users
//     whose ``organisation_id`` doesn't match the request's tenant),
//   * per-app DRF permission classes (e.g. ``ChatPermissionRequired``).
//
// In short: hiding a menu in React is UX, not security.
const _taskydoDomain = (import.meta.env.VITE_BASE_DOMAIN || 'tasky.do').toLowerCase().trim();
const _curediaDomain = (
  import.meta.env.VITE_CUREDIA_BASE_DOMAIN
  || (_taskydoDomain === 'localhost' || _taskydoDomain.endsWith('.localhost')
    ? 'curedia.localhost'
    : 'curedia.com')
).toLowerCase().trim();
const _letswebinarDomain = (
  import.meta.env.VITE_LETSWEBINAR_BASE_DOMAIN
  || (_taskydoDomain === 'localhost' || _taskydoDomain.endsWith('.localhost')
    ? 'letswebinar.localhost'
    : 'letswebinar.com')
).toLowerCase().trim();
const _successyatraDomain = (
  import.meta.env.VITE_SUCCESSYATRA_BASE_DOMAIN
  || (_taskydoDomain === 'localhost' || _taskydoDomain.endsWith('.localhost')
    ? 'successyatra.localhost'
    : 'successyatra.com')
).toLowerCase().trim();

export const HOST = {
  // Production apex for this build (override via VITE_BASE_DOMAIN).
  BASE_DOMAIN: _taskydoDomain,
  // Every product apex, longest match first in resolveApexDomain.
  PRODUCT_DOMAINS: [...new Set([
    _successyatraDomain, _letswebinarDomain, _curediaDomain, _taskydoDomain,
  ])].sort(
    (a, b) => b.length - a.length,
  ),
};

// Keep in sync with ``tenants.sanitization.RESERVED_PLATFORM_SUBDOMAINS``.
export const PLATFORM_SUBDOMAINS = new Set([
  'public', 'www', 'pub', 'connect', 'shopify', 'oauth',
  'api', 'admin', 'app', 'apps', 'static', 'media', 'cdn', 'assets',
  'graphql', 'ws', 'websocket', 'auth', 'login', 'sso', 'webhook', 'webhooks',
  'mail', 'smtp', 'imap', 'pop', 'ftp', 'ns', 'ns1', 'ns2', 'mx',
  'origin', 'edge', 'proxy', 'status', 'health', 'healthz',
  'staging', 'prod', 'production', 'localhost',
  'git', 'ci', 'vpn', 'deploy', 'm', 'mobile', 'docs', 'support',
  'superadmin', 'super-admin', 'signal', 'signalbridge',
]);

function normaliseHostname(hostname) {
  return (hostname || '').toLowerCase().trim();
}

function browserHostname() {
  if (typeof window !== 'undefined' && window.location) {
    return window.location.hostname || '';
  }
  return '';
}

function isLoopbackHostname(host) {
  return host === '127.0.0.1' || host === '[::1]' || host === '::1';
}

/**
 * Resolve the apex domain for a hostname.
 *
 * Prefer the build-time ``VITE_BASE_DOMAIN`` when it matches the current
 * host.  When that env var is missing or wrong (a common production
 * foot-gun — the bundle defaults to ``localhost``), infer the apex from
 * the hostname shape so ``psfl.tasky.do`` is never mistaken for public.
 */
export function resolveApexDomain(hostname) {
  const host = normaliseHostname(
    hostname || browserHostname(),
  );
  const configured = HOST.BASE_DOMAIN;
  const domains = HOST.PRODUCT_DOMAINS || [configured];

  if (!host) return configured;

  for (const domain of domains) {
    if (
      host === domain
      || host === `www.${domain}`
      || host.endsWith('.' + domain)
    ) {
      return domain;
    }
  }

  const parts = host.split('.');

  // ``www.<apex>.<tld>`` or ``<tenant>.<apex>.<tld>`` (tasky.do-style).
  if (parts.length === 3) {
    return parts.slice(-2).join('.');
  }

  // ``<tenant>.localhost`` dev pattern.
  if (parts.length === 2 && parts[1] === 'localhost') {
    return 'localhost';
  }

  // Bare apex (`tasky.do`) or single-label dev host (`localhost`).
  if (parts.length <= 2) {
    return host;
  }

  // Deeper nesting — treat the last two labels as the apex.
  return parts.slice(-2).join('.');
}

/**
 * True when the current browser host is the public/landing domain
 * (no tenant subdomain) — i.e. the super-admin host.
 *
 * Returns false on every tenant subdomain (`acme.tasky.do`,
 * `psfl.tasky.do`, …).  Returns true for the bare apex and its
 * ``www`` alias (`tasky.do`, `www.tasky.do`, `localhost`) and for
 * loopback IPs in dev so `runserver` works without per-developer config.
 */
export function isPublicHost(hostname) {
  const host = normaliseHostname(
    hostname || browserHostname(),
  );
  if (!host) return false;
  if (isLoopbackHostname(host)) return true;

  const apex = resolveApexDomain(host);

  // Apex and ``www`` alias — marketing site only.
  if (host === apex || host === `www.${apex}`) return true;

  // Platform hosts (Shopify Connect app, APIs, mail, …) are not tenants.
  if (host.endsWith('.' + apex)) {
    const slug = host.slice(0, -1 - apex.length).split('.')[0] || '';
    if (PLATFORM_SUBDOMAINS.has(slug)) return true;
    return false;
  }

  // Unrecognised hostname — default to public so a one-off dev host
  // still loads; tenant subdomains always match the rules above.
  return true;
}

/**
 * Extract the tenant slug from the current host, or `null` if we're on
 * the public host.  Cosmetic only — used by the AppShell to label the
 * tenant in the UI.  Never used for access decisions.
 */
export function tenantSlugFromHost(hostname) {
  const host = normaliseHostname(
    hostname || browserHostname(),
  );
  if (!host || isPublicHost(host)) return null;

  const apex = resolveApexDomain(host);
  if (host.endsWith('.' + apex)) {
    return host.slice(0, -1 - apex.length).split('.')[0] || null;
  }

  const parts = host.split('.');
  if (parts.length === 2 && parts[1] === 'localhost') {
    return parts[0] || null;
  }
  return null;
}

/**
 * Same normalisation as the Django workspace finder
 * (``tenants.sanitization.sanitize_sub_domain`` / ``landing.js``):
 * accept a pasted URL or hostname, then keep the leading DNS label.
 */
export function sanitiseOrgCode(raw) {
  let value = String(raw || '').trim();
  if (!value) return '';
  if (value.includes('//')) value = value.split('//')[1];
  value = value.split('/')[0].split(':')[0];
  value = value.split('.')[0];
  value = value
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (value.length > 63) value = value.slice(0, 63).replace(/-+$/, '');
  if (PLATFORM_SUBDOMAINS.has(value)) return '';
  return value;
}

/**
 * Absolute URL on a tenant subdomain of the current apex, e.g.
 * ``https://acme.tasky.do/password-recovery``.
 */
export function workspaceHostUrl(slug, path = '/login') {
  const apex = resolveApexDomain();
  const { protocol, port } = window.location;
  const portSuffix = port ? `:${port}` : '';
  const normalised = path.startsWith('/') ? path : `/${path}`;
  return `${protocol}//${slug}.${apex}${portSuffix}${normalised}`;
}


// ---------------------------------------------------------------------------
// 2. Backend / API
// ---------------------------------------------------------------------------
// `BASE_URL` empty string  -> same-origin requests (`/api/...`) — used in
// production when the React build is served by Django itself.
// During `npm run dev` Vite proxies `/api` to the Django dev server (see
// vite.config.js), so the empty default just works there too.
/**
 * Host the browser should call for ``/api`` and ``/ws``.
 *
 * Taskydo pages stay on their own host. Curedia pages call the matching
 * ``*.tasky.do`` host. One Django project serves both.
 */
export function apiOrigin() {
  if (typeof window === 'undefined' || !window.location) return '';
  const override = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
  if (override) return override;
  return apiOriginFromLocation(window.location, {
    taskydoDomain: _taskydoDomain,
    curediaDomain: _curediaDomain,
  });
}

export const API = {
  BASE_URL: (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, ''),

  // Mirrors the URL map in docs/rest_api.md exactly.
  ENDPOINTS: {
    // ---- auth ----
    LOGIN:          '/api/super_admin/login/',
    PUBLIC_SIGNUP:  '/api/super_admin/public-signup/',
    PASSWORD_RESET: '/api/super_admin/password-reset/',
    PASSWORD_RESET_CONFIRM: '/api/super_admin/password-reset/confirm/',
    LOGOUT:         '/api/super_admin/logout/',
    ME:             '/api/super_admin/me/',
    TOKEN:          '/api/super_admin/token/',
    TOKEN_REFRESH:  '/api/super_admin/token/refresh/',
    TOKEN_BLACKLIST:'/api/super_admin/token/blacklist/',
    LOGIN_SECURITY_CLIENT_CONFIG: '/api/super_admin/login-security/client-config/',
    LOGIN_SECURITY_CONFIG: '/api/super_admin/login-security/',
    DB_VIEWER: '/api/super_admin/db-viewer/',
    DB_VIEWER_SCHEMA: (alias, schema) =>
      `/api/super_admin/db-viewer/${encodeURIComponent(alias)}/${encodeURIComponent(schema)}/`,
    DB_VIEWER_TABLE: (alias, schema, table) =>
      `/api/super_admin/db-viewer/${encodeURIComponent(alias)}/${encodeURIComponent(schema)}/${encodeURIComponent(table)}/`,
    DB_VIEWER_UPDATE_CELL: (alias, schema, table) =>
      `/api/super_admin/db-viewer/${encodeURIComponent(alias)}/${encodeURIComponent(schema)}/${encodeURIComponent(table)}/update-cell/`,
    DB_VIEWER_ADD_ROW: (alias, schema, table) =>
      `/api/super_admin/db-viewer/${encodeURIComponent(alias)}/${encodeURIComponent(schema)}/${encodeURIComponent(table)}/add-row/`,
    DB_VIEWER_DELETE_ROW: (alias, schema, table) =>
      `/api/super_admin/db-viewer/${encodeURIComponent(alias)}/${encodeURIComponent(schema)}/${encodeURIComponent(table)}/delete-row/`,
    // ---- self-service profile ----
    // Backed by ``UserViewSet.update_me`` and
    // ``UserViewSet.set_my_password`` — see ``super_admin/api_views.py``.
    // The UPDATE endpoint accepts ``multipart/form-data`` so the
    // user can upload a new profile picture in the same request.
    ME_UPDATE:      '/api/super_admin/users/me/update/',
    ME_SET_PWD:     '/api/super_admin/users/me/set-password/',


    // ---- tasks (mounted at /api/task/ which mirrors /task/) ----
    TASKS:          '/api/task/tasks/',
    TASK_DETAIL:    (id) => `/api/task/tasks/${id}/`,
    TASK_STATUS:    (id) => `/api/task/tasks/${id}/update-status/`,
    TASK_SUMMARY:   '/api/task/tasks/summary/',
    ROUTINE_TASKS:           '/api/task/routine-tasks/',
    ROUTINE_TASK_DETAIL:     (id) => `/api/task/routine-tasks/${id}/`,
    ROUTINE_TASK_RUN_NOW:    (id) => `/api/task/routine-tasks/${id}/run-now/`,
    // Excel round-trip endpoints — see ``RoutineTaskViewSet.export``,
    // ``.template`` and ``.import_xlsx`` in
    // ``task_management/api_views.py``.  ``EXPORT`` and ``TEMPLATE``
    // stream .xlsx blobs (Content-Disposition: attachment) so the
    // axios callers in ``RoutineTasksAPI`` set ``responseType: 'blob'``.
    ROUTINE_TASK_EXPORT:     '/api/task/routine-tasks/export/',
    ROUTINE_TASK_TEMPLATE:   '/api/task/routine-tasks/template/',
    ROUTINE_TASK_IMPORT:     '/api/task/routine-tasks/import/',
    TASK_COMMENTS:  '/api/task/comments/',
    USER_TASKS:     '/api/task/user-tasks/',


    // ---- complaints (mounted at /api/complaints/) ----
    COMPLAINTS:         '/api/complaints/complaints/',
    COMPLAINT_DETAIL:   (id) => `/api/complaints/complaints/${id}/`,
    COMPLAINT_STATUS:   (id) => `/api/complaints/complaints/${id}/update-status/`,
    COMPLAINT_REASSIGN: (id) => `/api/complaints/complaints/${id}/reassign/`,
    COMPLAINT_SUMMARY:  '/api/complaints/complaints/summary/',
    COMPLAINT_COMMENTS: '/api/complaints/complaint-comments/',

    // ---- notifications (mounted at /api/notification/) ----
    // Aggregated "bell" feed for the top bar — see
    // ``NotificationInboxView`` in ``notification/api_views.py``.
    NOTIFICATION_INBOX:        '/api/notification/inbox/',

    // ---- chat (mounted at /api/chat/) ----
    CHAT_CONVERSATIONS:        '/api/chat/conversations/',

    CHAT_CONVERSATION_DETAIL:  (id) => `/api/chat/conversations/${id}/`,
    CHAT_MARK_READ:            (id) => `/api/chat/conversations/${id}/mark-read/`,
    CHAT_MESSAGES:             '/api/chat/messages/',
    CHAT_CONTACTS:             '/api/chat/contacts/',
    CHAT_CONTACT_DETAIL:       (id) => `/api/chat/contacts/${id}/`,

    // ---- shared (orgs / users / divisions used by forms) ----
    USERS:          '/api/super_admin/users/',
    USER_DETAIL:    (id) => `/api/super_admin/users/${id}/`,
    USER_SET_PWD:   (id) => `/api/super_admin/users/${id}/set-password/`,
    ORGANISATIONS:  '/api/super_admin/organisations/',
    ORGANISATION_DETAIL: (id) => `/api/super_admin/organisations/${id}/`,
    ORGANISATION_LOCK_DETAILS: (id) => `/api/super_admin/organisations/${id}/lock-details/`,
    ORGANISATION_UNLOCK_DETAILS: (id) => `/api/super_admin/organisations/${id}/unlock-details/`,
    // Lightweight server-side autocomplete for the Create-User
    // organisation dropdown.  Backed by
    // ``OrganisationViewSet.lookup`` — paginated to 10 results, reads
    // from the public schema, returns minimal fields per option.
    ORGANISATION_LOOKUP: '/api/super_admin/organisations/lookup/',
    ORGANISATION_MODULES: (id) => `/api/super_admin/organisations/${id}/modules/`,
    MODULE_DEFAULTS: '/api/super_admin/module-defaults/',
    SUCCESSYATRA_WORKSPACES: '/api/super_admin/successyatra/workspaces/',
    SUCCESSYATRA_MEMBERS: '/api/super_admin/successyatra/members/',
    SUCCESSYATRA_COMPANIES: '/api/super_admin/successyatra/companies/',
    SUCCESSYATRA_MOVE: '/api/super_admin/successyatra/move/',

    ROLES:          '/api/super_admin/roles/',
    ROLE_DETAIL:    (id) => `/api/super_admin/roles/${id}/`,
    PERMISSIONS:    '/api/super_admin/permissions/',
    PERMISSION_DETAIL: (id) => `/api/super_admin/permissions/${id}/`,
    HIERARCHIES:    '/api/super_admin/organisational-hierarchies/',
    HIERARCHY_DETAIL: (id) => `/api/super_admin/organisational-hierarchies/${id}/`,
    ROLE_PERMISSIONS: '/api/super_admin/role-permissions/',
    ROLE_PERMISSION_DETAIL: (id) => `/api/super_admin/role-permissions/${id}/`,
    MODULES:        '/api/super_admin/modules/',
    MODULE_DETAIL:  (id) => `/api/super_admin/modules/${id}/`,
    CONTROLS:       '/api/super_admin/controls/',
    CONTROL_DETAIL: (id) => `/api/super_admin/controls/${id}/`,

    // ---- per-user module access (Business → Access Control) ----
    // Backed by ``UserModuleAccessViewSet`` in
    // ``super_admin/api_views.py``.  ``MATRIX`` returns the full
    // {modules, levels, users[]} grid the Access Control screen renders
    // in one round-trip; ``BULK_SET`` replaces one user's whole
    // module-access map atomically.  Both are org-admin only.
    MODULE_ACCESS:         '/api/super_admin/module-access/',
    MODULE_ACCESS_MATRIX:  '/api/super_admin/module-access/matrix/',
    MODULE_ACCESS_BULK_SET:'/api/super_admin/module-access/bulk-set/',
    MODULE_ACCESS_COPY:    '/api/super_admin/module-access/copy/',

    // ---- Pharma SFA fine-grained per-screen access (Access Control) ----
    // Pharma is special: instead of a single Admin/User/View/None level,
    // each screen carries its own CRUD grid.  Backed by
    // ``PharmaScreenAccessViewSet`` in
    // ``super_admin/pharma_access_views.py``.  ``CATALOG`` returns the
    // static tab→screen→actions structure; ``MATRIX`` returns every
    // user's per-screen grid; ``BULK_SET`` replaces one user's whole
    // grid atomically; ``COPY`` mirrors one user's grid onto another.
    // All org-admin only.
    PHARMA_ACCESS_CATALOG:  '/api/super_admin/pharma-access/catalog/',
    PHARMA_ACCESS_MATRIX:   '/api/super_admin/pharma-access/matrix/',
    PHARMA_ACCESS_BULK_SET: '/api/super_admin/pharma-access/bulk-set/',
    PHARMA_ACCESS_COPY:     '/api/super_admin/pharma-access/copy/',

    // ---- Per-module screen CRUD access (Access Control) ----
    // Generalises the Pharma-only endpoints above to every module.
    // Backed by ``ModuleScreenAccessViewSet`` in
    // ``super_admin/module_screen_access_views.py``.
    SCREEN_ACCESS_MODULES:  '/api/super_admin/screen-access/modules/',
    SCREEN_ACCESS_CATALOG:  '/api/super_admin/screen-access/catalog/',
    SCREEN_ACCESS_MATRIX:   '/api/super_admin/screen-access/matrix/',
    SCREEN_ACCESS_BULK_SET: '/api/super_admin/screen-access/bulk-set/',
    SCREEN_ACCESS_COPY:     '/api/super_admin/screen-access/copy/',

    // ---- Permission user groups (Business → Access Control) ----
    PERMISSION_USER_GROUPS:        '/api/super_admin/permission-user-groups/',
    PERMISSION_USER_GROUP_DETAIL:  (id) => `/api/super_admin/permission-user-groups/${id}/`,
    PERMISSION_USER_GROUPS_MATRIX: '/api/super_admin/permission-user-groups/matrix/',
    PERMISSION_USER_GROUP_MEMBERS: (id) => `/api/super_admin/permission-user-groups/${id}/set-members/`,
    PERMISSION_USER_GROUP_MODULE_BULK: (id) => `/api/super_admin/permission-user-groups/${id}/module-access/bulk-set/`,
    PERMISSION_USER_GROUP_PHARMA_BULK: (id) => `/api/super_admin/permission-user-groups/${id}/pharma-access/bulk-set/`,




    // ---- business / organisational_admin (mounted at /api/admin/) ----
    DIVISIONS:        '/api/admin/divisions/',
    DIVISION_DETAIL:  (id) => `/api/admin/divisions/${id}/`,
    SUB_DIVISIONS:       '/api/admin/sub-divisions/',
    SUB_DIVISION_DETAIL: (id) => `/api/admin/sub-divisions/${id}/`,

    GROUPS:           '/api/admin/groups/',
    GROUP_DETAIL:     (id) => `/api/admin/groups/${id}/`,
    HR_MANAGEMENTS:   '/api/admin/hr-managements/',
    HR_MANAGEMENT_DETAIL: (id) => `/api/admin/hr-managements/${id}/`,
    NOTICE_TEMPLATES: '/api/admin/notice-templates/',
    NOTICE_TEMPLATE_DETAIL: (id) => `/api/admin/notice-templates/${id}/`,
    PROJECTS:         '/api/admin/projects/',
    PROJECT_DETAIL:   (id) => `/api/admin/projects/${id}/`,
    DEPARTMENTS:      '/api/admin/departments/',
    DEPARTMENT_DETAIL:(id) => `/api/admin/departments/${id}/`,
    DEPOTS:           '/api/admin/depots/',
    DEPOT_DETAIL:     (id) => `/api/admin/depots/${id}/`,
    PRODUCTS:         '/api/admin/products/',
    PRODUCT_DETAIL:   (id) => `/api/admin/products/${id}/`,
    PUBLIC_COMPLAINT_ADMINS: '/api/admin/public-complaint-admins/',
    PUBLIC_COMPLAINT_ADMIN_DETAIL: (id) => `/api/admin/public-complaint-admins/${id}/`,

    // Default-fixtures inspector / seeder.  Backed by
    // ``organisational_admin.api_defaults`` and consumed by the
    // SPA's Business → Default tab.  GET reports the per-fixture
    // status; POST seeds a single group ("roles" / "permissions" /
    // "divisions" / "external-users") or "all".  Both calls are
    // tenant-scoped and require the caller to be an org admin (or
    // super-admin) — see ``api_defaults._is_org_admin``.
    BUSINESS_DEFAULTS:        '/api/admin/defaults/',
    BUSINESS_DEFAULTS_SEED:   '/api/admin/defaults/seed/',
    BUSINESS_GOOGLE_SHEETS:   '/api/admin/integrations/google-sheets/',
    BUSINESS_PUBLIC_COMPLAINT_ACCESS: '/api/admin/public-complaint-access/',
    BUSINESS_COMPLAINT_SERIAL: '/api/admin/complaint-serial/',
    BUSINESS_TIMEZONE: '/api/admin/timezone/',
    // One organisation schema. Never the whole database.
    BUSINESS_SCHEMA_BACKUP: '/api/admin/schema-backup/',
    BUSINESS_SCHEMA_BACKUP_DOWNLOAD: '/api/admin/schema-backup/download/',
    BUSINESS_SCHEMA_BACKUP_RESTORE: '/api/admin/schema-backup/restore/',
    // Name and logo shown inside the mobile app after sign-in.
    // The store listing stays taskydo. See
    // ``organisational_admin.api_mobile_branding``.
    BUSINESS_MOBILE_BRANDING: '/api/admin/mobile-branding/',

    // ---- pharma SFA (mounted at /api/pharma/) ----
    PHARMA_DOCTORS:        '/api/pharma/doctors/',
    PHARMA_CHEMISTS:       '/api/pharma/chemists/',
    PHARMA_STOCKISTS:      '/api/pharma/stockists/',
    PHARMA_HOSPITALS:      '/api/pharma/hospitals/',
    PHARMA_PRODUCTS:       '/api/pharma/products/',
    PHARMA_SPECIALTIES:    '/api/pharma/specialties/',
    PHARMA_DIVISIONS:      '/api/pharma/divisions/',
    PHARMA_TOUR_PROGRAMS:  '/api/pharma/tour-programs/',
    PHARMA_DAY_PLANS:      '/api/pharma/day-plans/',
    PHARMA_DCRS:           '/api/pharma/dcrs/',
    PHARMA_VISITS:         '/api/pharma/visits/',
    PHARMA_POBS:           '/api/pharma/pobs/',
    PHARMA_PRIMARY_SALES:  '/api/pharma/primary-sales/',
    PHARMA_PAYMENTS:       '/api/pharma/payments/',
    PHARMA_STOCKIST_STOCK: '/api/pharma/stockist-stocks/',
    PHARMA_SECONDARY_SALES:'/api/pharma/secondary-sales/',
    PHARMA_CHEMIST_SALES:  '/api/pharma/chemist-sales/',
    PHARMA_RCPA:           '/api/pharma/rcpa/',
    PHARMA_SAMPLES:        '/api/pharma/sample-distributions/',
    PHARMA_EXPENSES:       '/api/pharma/expenses/',
    PHARMA_LEADS:          '/api/pharma/leads/',
    PHARMA_TARGETS:        '/api/pharma/targets/',
    PHARMA_CAMPAIGNS:      '/api/pharma/campaigns/',
    PHARMA_FIELD_USERS:    '/api/pharma/field-users/',
    // Designation list, used for the "role" dropdowns on the Hierarchy,
    // Expense and Approval masters.  The router prefix really is
    // "pharma-roles" (see ``curedia/api_urls.py``) — unlike
    // ``specialties`` / ``divisions`` above, which are unprefixed.
    PHARMA_ROLES:          '/api/pharma/pharma-roles/',
    PHARMA_VISUAL_AIDS:    '/api/pharma/visual-aids/',

    PHARMA_GPS_PINGS:      '/api/pharma/gps-pings/',
    PHARMA_NOTIFICATIONS:  '/api/pharma/notifications/',
    // Analytics URL paths must match the @action(url_path=...)
    // values declared on AnalyticsViewSet in
    // ``curedia/api_views.py`` exactly — DRF does NOT auto-alias
    // these.  If you rename one, update both ends in lockstep.
    // The three-level Home dashboard (admin / manager / MR).  One call
    // returns the month's KPIs, detail tables, month options, the team
    // breakdown and the org's thresholds — see
    // ``AnalyticsViewSet.home_dashboard``.
    PHARMA_HOME_DASHBOARD:      '/api/pharma/analytics/home-dashboard/',
    PHARMA_MY_TEAM:             '/api/pharma/analytics/my-team/',
    // Admin-only SFA configuration.  ``UPDATE`` is a separate action
    // route because the viewset is a plain ViewSet (single row per org),
    // not a ModelViewSet with a detail URL.
    PHARMA_SFA_CONFIG:          '/api/pharma/sfa-config/',
    PHARMA_SFA_CONFIG_UPDATE:   '/api/pharma/sfa-config/update/',
    PHARMA_ANALYTICS_DASHBOARD: '/api/pharma/analytics/ready-reckoner/',
    PHARMA_ANALYTICS_CALL_AVG:  '/api/pharma/analytics/call-cycle-breaches/',
    PHARMA_ANALYTICS_TP_DEV:    '/api/pharma/analytics/tp-deviation/',
    PHARMA_ANALYTICS_VIP_REPORT:'/api/pharma/analytics/vip-doctors/',
    PHARMA_ANALYTICS_FORECAST:  '/api/pharma/analytics/forecast/',
    PHARMA_ANALYTICS_EXPORT_DCR:'/api/pharma/analytics/export-dcr/',
    PHARMA_DCR_VERIFY:     (id) => `/api/pharma/dcrs/${id}/verify/`,
    PHARMA_DCR_SUBMIT:     (id) => `/api/pharma/dcrs/${id}/submit/`,
    PHARMA_DCR_SUBMIT_POLICY: '/api/pharma/dcrs/submit-policy/',
    PHARMA_DCR_OFFLINE:    '/api/pharma/dcrs/offline-sync/',
    PHARMA_TP_SUBMIT:      (id) => `/api/pharma/tour-programs/${id}/submit/`,
    PHARMA_TP_APPROVE:     (id) => `/api/pharma/tour-programs/${id}/approve/`,
    PHARMA_TP_REJECT:      (id) => `/api/pharma/tour-programs/${id}/reject/`,
    PHARMA_POB_INVOICE:    (id) => `/api/pharma/pobs/${id}/generate-invoice/`,
    PHARMA_EXPENSE_FROM_DCR: '/api/pharma/expenses/from-dcr/',
    // ---- Chatbot, Biometric, Territory alignment, Non-listed ----
    PHARMA_CHATBOT_ASK:        '/api/pharma/chatbot/ask/',
    PHARMA_CHATBOT_HISTORY:    '/api/pharma/chatbot/history/',
    PHARMA_BIOMETRIC_REG_OPTIONS: '/api/pharma/biometric/register-options/',
    PHARMA_BIOMETRIC_REGISTER: '/api/pharma/biometric/register/',
    PHARMA_BIOMETRIC_ASSERT_OPTIONS: '/api/pharma/biometric/assertion-options/',
    PHARMA_BIOMETRIC_VERIFY:   '/api/pharma/biometric/verify/',
    PHARMA_BIOMETRIC_CREDS:    '/api/pharma/biometric/credentials/',
    PHARMA_TERRITORY_DIAGNOSE: '/api/pharma/territory-alignment/diagnose/',
    PHARMA_TERRITORY_PREVIEW:  '/api/pharma/territory-alignment/preview-rebalance/',
    PHARMA_TERRITORY_REBALANCE:'/api/pharma/territory-alignment/rebalance/',
    PHARMA_VISIT_PROMOTE:      (id) => `/api/pharma/visits/${id}/promote-non-listed/`,
    PHARMA_LEAD_DISTRIBUTE:    '/api/pharma/leads/distribute/',
    PHARMA_LEAD_EXPLAIN:       (id) => `/api/pharma/leads/${id}/explain-assignment/`,

    // ---- Configuration masters (mounted at /api/pharma/masters/) ----
    //
    // The ten masters from the brief.  Three of them predate this group
    // and keep their original paths, so they are NOT repeated here:
    //   user master    -> PHARMA_FIELD_USERS
    //   product master -> PHARMA_PRODUCTS
    //   target master  -> PHARMA_TARGETS
    PHARMA_M_HIERARCHY:      '/api/pharma/masters/hierarchy/',
    PHARMA_M_HIERARCHY_CHART:'/api/pharma/masters/hierarchy/chart/',
    PHARMA_M_TAX:            '/api/pharma/masters/tax/',
    PHARMA_M_EXPENSE_HEADS:  '/api/pharma/masters/expense-heads/',
    PHARMA_M_HEADQUARTERS:   '/api/pharma/masters/headquarters/',
    PHARMA_M_TERRITORY_LEGS: '/api/pharma/masters/territory-legs/',
    PHARMA_M_PRICE_LISTS:    '/api/pharma/masters/price-lists/',
    PHARMA_M_PRICE_ITEMS:    '/api/pharma/masters/price-list-items/',
    PHARMA_M_APPROVAL_RULES: '/api/pharma/masters/approval-rules/',
    PHARMA_M_APPROVAL_DOCS:  '/api/pharma/masters/approval-rules/document-types/',
    PHARMA_M_TERRITORIES:    '/api/pharma/territories/',
    PHARMA_M_IMPORT_BATCHES: '/api/pharma/masters/import-batches/',
    PHARMA_M_PENDING_SUMMARY:'/api/pharma/masters/import-batches/pending-summary/',

    // ---- Excel: sample template / upload / export ----
    //
    // Built from a resource's own list URL so one helper serves every
    // master.  Pass any collection URL from this file, e.g.
    //   PHARMA_EXCEL_SAMPLE(API.PHARMA_DOCTORS)
    // Keeping these as functions (rather than 30 hard-coded constants)
    // means adding a master needs no new endpoint entries.
    PHARMA_EXCEL_SAMPLE: (base) => `${base}sample-excel/`,
    PHARMA_EXCEL_UPLOAD: (base) => `${base}upload-excel/`,
    PHARMA_EXCEL_EXPORT: (base) => `${base}export-excel/`,

    // ---- Master-data approvals (doctors / chemists / stockists) ----
    PHARMA_PENDING:      (base) => `${base}pending/`,
    PHARMA_APPROVE:      (base, id) => `${base}${id}/approve/`,
    PHARMA_REJECT:       (base, id) => `${base}${id}/reject/`,
    PHARMA_BULK_APPROVE: (base) => `${base}bulk-approve/`,

    WABA_HELP: '/api/waba-chat/help/',
    WABA_INBOX_POLL: '/api/waba-chat/inbox/poll/',
    RAG_HELP: '/api/rag/help/',
    RAG_TEST_CHAT: '/api/rag/test-chat/',
    RAG_DASHBOARD: '/api/rag/dashboard/',
    SIGNALBRIDGE_HELP: '/api/signalbridge/help/',
    SIGNALBRIDGE_DASHBOARD: '/api/signalbridge/dashboard/',
  },




  TIMEOUT_MS: 30_000,
};

// ---------------------------------------------------------------------------
// 3. Local storage keys (kept in one place to avoid typos)
// ---------------------------------------------------------------------------
export const STORAGE_KEYS = {
  ACCESS:  'taskydo.access',
  REFRESH: 'taskydo.refresh',
  USER:    'taskydo.user',
  /** Last three people this browser assigned a task to (search-select recents). */
  RECENT_TASK_ASSIGNEES: 'taskydo.recentTaskAssignees',
};

// ---------------------------------------------------------------------------
// 4. Routes (the URL paths the React Router serves)
// ---------------------------------------------------------------------------
// We deliberately mirror the existing Django URL prefixes:
//   /task/...        -> Tasks module
//   /complaints/...  -> Complaints module
// so that bookmarks, deep links and existing emails keep working when the
// React build replaces the server-rendered pages.
export const ROUTES = {
  ROOT:             '/',
  LOGIN:            '/login',
  SIGNUP:           '/signup',
  // Public self-service password recovery.  Request form plus the
  // emailed ``?uid=&token=`` confirm step live on the same screen.
  PASSWORD_RECOVERY: '/password-recovery',
  // Shared friendly error/denial page.  Hit programmatically by the
  // axios 403 handler and rendered inline by RequirePermission /
  // LandingRedirect / the catch-all ``*`` route.
  ACCESS_DENIED:    '/access-denied',

  // Self-service profile page.  Reachable from the user's own
  // name / avatar in the AppShell top bar, and from the deep link
  // shared in support emails.  Permitted to every authenticated
  // user (no per-app permission gate) — they're only ever editing
  // their OWN ``super_admin.User`` row, enforced server-side by
  // ``UserViewSet.update_me`` (see ``super_admin/api_views.py``).
  PROFILE:          '/me',


  // Auretics → Taskydo SSO landing.  PUBLIC route — the JWT in the
  // ``?token=`` query param IS the credential, so the page must be
  // reachable without an existing session.  Mirrors the legacy
  // server-rendered URL ``/complaints/sso/login/`` so existing
  // Auretics deep-links keep working under the React SPA.  See
  // ``react_frontend/src/pages/sso/SSOLogin.jsx`` and
  // ``complaints_management_system/views.api_sso_login``.
  SSO_LOGIN:        '/complaints/sso/login',



  // Tasks
  TASKS_HOME:       '/task',
  TASKS_LIST:       '/task/task-list/:filter',
  TASK_DETAILS:     '/task/task/:id',
  TASK_CREATE:      '/task/create-task',
  TASK_EDIT:        '/task/edit-task/:id',

  // Routine (recurring) tasks — share the Tasks app's permission gate.
  // Routine tasks are *templates* the cron worker expands into normal
  // Task rows on the configured cadence.  Listing / create / edit
  // pages live under ``/task/routine-tasks`` so they show up in the
  // Tasks app's top-bar without needing a brand-new app key.
  ROUTINE_TASKS_LIST:   '/task/routine-tasks',
  ROUTINE_TASK_NEW:     '/task/routine-tasks/new',
  // Excel import page — host the upload form and the on-screen
  // instructions in one place.  Served by ``RoutineTaskImport.jsx``.
  // Listed BEFORE the parametric ``ROUTINE_TASK_EDIT`` route so
  // React Router doesn't accidentally treat ``import`` as a numeric id.
  ROUTINE_TASK_IMPORT:  '/task/routine-tasks/import',
  ROUTINE_TASK_EDIT:    '/task/routine-tasks/:id/edit',


  // Complaints
  COMPLAINTS_HOME:  '/complaints',
  COMPLAINTS_LIST:  '/complaints/list/:filter',
  COMPLAINT_DETAILS:'/complaints/details/:id',
  COMPLAINT_CREATE: '/complaints/create',
  COMPLAINT_EDIT:   '/complaints/edit/:id',

  // Chat
  CHAT_HOME:        '/chat',
  CHAT_CONVERSATION:'/chat/:conversationId',

  // Business / Organisation Admin
  BUSINESS_HOME:        '/business',
  BUSINESS_RESOURCE:    '/business/:resource',
  BUSINESS_RESOURCE_NEW:'/business/:resource/new',
  BUSINESS_RESOURCE_EDIT:'/business/:resource/:id/edit',

  // Uni Email — engagement module ported from external project.
  // Tenant-only.  See ``react_frontend/src/pages/uni/UniEmailPages.jsx``
  // for the page set and ``uni_email/api_urls.py`` for the backend
  // contract.
  UNI_EMAIL_HOME:       '/uni-email',
  UNI_EMAIL_CONTACTS:   '/uni-email/contacts',
  UNI_EMAIL_LISTS:      '/uni-email/lists',
  UNI_EMAIL_TEMPLATES:  '/uni-email/templates',
  UNI_EMAIL_CAMPAIGNS:  '/uni-email/campaigns',

  // Uni SMS — DLT-compliant SMS module.  Tenant-only.
  UNI_SMS_HOME:           '/uni-sms',
  UNI_SMS_CONTACTS:       '/uni-sms/contacts',
  UNI_SMS_LISTS:          '/uni-sms/lists',
  UNI_SMS_DLT_TEMPLATES:  '/uni-sms/dlt-templates',
  UNI_SMS_CAMPAIGNS:      '/uni-sms/campaigns',

  // Uni Forms — form / quiz / poll builder.  Tenant-only.
  UNI_FORMS_HOME:       '/uni-forms',
  UNI_FORMS_FORMS:      '/uni-forms/forms',
  // "For Me" — forms somebody else picked THIS user for, under
  // "Display this form for selected internal users".  A respondent-side
  // screen, not a builder one.
  UNI_FORMS_FOR_ME:     '/uni-forms/for-me',
  // "My Submissions" — forms THIS user has actually filled. Own rows only.
  UNI_FORMS_MY_SUBMISSIONS: '/uni-forms/my-submissions',
  UNI_FORMS_QUIZZES:    '/uni-forms/quizzes',
  UNI_FORMS_POLLS:      '/uni-forms/polls',
  UNI_FORMS_TEMPLATES:  '/uni-forms/templates',
  UNI_FORMS_AUDIENCE:   '/uni-forms/audience',
  UNI_FORMS_FIGURES:   '/uni-forms/figures',
  UNI_FORMS_REPORTS:    '/uni-forms/reports',
  UNI_FORMS_REPORT_TEMPLATES: '/uni-forms/report-templates',
  UNI_FORMS_REPORT_TEMPLATE_NEW: '/uni-forms/report-templates/new',
  UNI_FORMS_REPORT_TEMPLATE_EDIT: (id = ':id') => `/uni-forms/report-templates/${id}/edit`,
  // "Get responses" screens — one per collection.  Kept as functions
  // because they're always built around a specific row's id, and having
  // the pattern in one place stops the list pages hand-concatenating a
  // path that then drifts from the <Route>.
  UNI_FORMS_FORM_RESPONSES: (id = ':formId') => `/uni-forms/forms/${id}/responses`,
  // The question builder — what the form actually *asks*.  Sits under the
  // form because a question has no life of its own; the screen is reached
  // from the "Questions" action on the Forms list.
  UNI_FORMS_FORM_QUESTIONS: (id = ':formId') => `/uni-forms/forms/${id}/questions`,
  UNI_FORMS_QUIZ_RESPONSES: (id = ':quizId') => `/uni-forms/quizzes/${id}/responses`,
  UNI_FORMS_POLL_RESULTS:   (id = ':pollId') => `/uni-forms/polls/${id}/results`,

  // WhatsApp Business — tenant-only. React + mobile own the UI.
  WABA_HOME:           '/waba-chat',
  WABA_INBOX:          '/waba-chat/inbox',
  WABA_THREAD:         '/waba-chat/inbox/:conversationId',
  WABA_CONTACTS:       '/waba-chat/contacts',
  WABA_LISTS:          '/waba-chat/lists',
  WABA_TAGS:           '/waba-chat/tags',
  WABA_LABELS:         '/waba-chat/labels',
  WABA_BROADCASTS:     '/waba-chat/broadcasts',
  WABA_TEMPLATES:      '/waba-chat/templates',
  WABA_MEDIA:          '/waba-chat/media',
  WABA_ANALYTICS:      '/waba-chat/analytics',
  WABA_WEBHOOKS:       '/waba-chat/webhooks',
  WABA_INCOMING_LOGS:  '/waba-chat/incoming-logs',
  WABA_SETTINGS:       '/waba-chat/settings',
  WABA_NUMBERS:        '/waba-chat/numbers',
  WABA_S3:             '/waba-chat/s3',
  WABA_RETRY:          '/waba-chat/retry',
  WABA_THROTTLE:       '/waba-chat/throttle',
  WABA_FAILED:         '/waba-chat/failed',
  WABA_AUTOMATION:     '/waba-chat/automation',
  WABA_FLOWS:          '/waba-chat/flows',
  WABA_FLOW_BUILDER:   '/waba-chat/flows/:id',
  WABA_API_KEYS:       '/waba-chat/api-keys',
  WABA_QUICK_REPLIES:  '/waba-chat/quick-replies',
  WABA_AUTO_REPLIES:   '/waba-chat/auto-replies',
  WABA_TEAMS:          '/waba-chat/teams',
  WABA_FORM_DIALOGS:   '/waba-chat/form-dialogs',
  WABA_FORM_SESSIONS:  '/waba-chat/form-sessions',
  WABA_KYC:            '/waba-chat/kyc',
  WABA_NOTES:          '/waba-chat/notes',
  WABA_HEALTH:         '/waba-chat/health',
  WABA_HOOKS:          '/waba-chat/hooks',
  WABA_HELP:           '/waba-chat/help',
  WABA_FEATURES:       '/waba-chat/features',

  // RAG knowledge / AI — tenant-only.
  RAG_HOME:            '/rag',
  RAG_KNOWLEDGE:       '/rag/knowledge',
  RAG_CATEGORIES:      '/rag/categories',
  RAG_VERSIONS:        '/rag/versions',
  RAG_CHUNKS:          '/rag/chunks',
  RAG_TRAINING:        '/rag/training',
  RAG_QUALITY:         '/rag/quality',
  RAG_FAQ:             '/rag/faq',
  RAG_CHATS:           '/rag/chats',
  RAG_CONVERSATIONS:   '/rag/conversations',
  RAG_CUSTOMERS:       '/rag/customers',
  RAG_SALES:           '/rag/sales',
  RAG_ESCALATION:      '/rag/escalation',
  RAG_FOLLOWUPS:       '/rag/followups',
  RAG_SEQUENCES:       '/rag/sequences',
  RAG_SEQUENCE_STEPS:  '/rag/sequence-steps',
  RAG_TRACKERS:        '/rag/trackers',
  RAG_REPORTS:         '/rag/reports',
  RAG_HEALTH_REPORTS:  '/rag/health-reports',
  RAG_PRODUCTS:        '/rag/products',
  RAG_PLAYGROUND:      '/rag/playground',
  RAG_TEST_CHAT:       '/rag/test-chat',
  RAG_SYSTEMS:         '/rag/systems',
  RAG_CONFIG:          '/rag/config',
  RAG_CONFIG_KEYS:     '/rag/config/keys',
  RAG_COSTS:           '/rag/costs',
  RAG_AUDIT:           '/rag/audit',
  RAG_ALERTS:          '/rag/alerts',
  RAG_COMPLIANCE:      '/rag/compliance',
  RAG_RESP_TPL:        '/rag/response-templates',
  RAG_DOC_TPL:         '/rag/document-templates',
  RAG_FEEDBACK:        '/rag/feedback',
  RAG_NOTES:           '/rag/notes',
  RAG_SHOPIFY:         '/rag/shopify',
  RAG_TEAM:            '/rag/team',
  RAG_HOOKS:           '/rag/hooks',
  RAG_HELP:            '/rag/help',
  RAG_FEATURES:        '/rag/features',
  RAG_AUTOMATION:      '/rag/automation',

  SIGNALBRIDGE_HOME:       '/signalbridge',
  SIGNALBRIDGE_PIXELS:     '/signalbridge/pixels',
  SIGNALBRIDGE_STORES:     '/signalbridge/stores',
  SIGNALBRIDGE_PARAMETERS: '/signalbridge/parameters',
  SIGNALBRIDGE_TEST:       '/signalbridge/test-event',
  SIGNALBRIDGE_RESEND:     '/signalbridge/resend',
  SIGNALBRIDGE_UPLOAD:     '/signalbridge/upload',
  SIGNALBRIDGE_BATCH:      '/signalbridge/uploads/:id',
  SIGNALBRIDGE_EVENTS:     '/signalbridge/events',
  SIGNALBRIDGE_EVENT:      '/signalbridge/events/:id',
  SIGNALBRIDGE_TEMPLATES:  '/signalbridge/templates',
  SIGNALBRIDGE_MIDDLEWARE: '/signalbridge/middleware',
  SIGNALBRIDGE_PAYLOADS:   '/signalbridge/payloads',
  SIGNALBRIDGE_HEALTH:     '/signalbridge/health',
  SIGNALBRIDGE_HELP:       '/signalbridge/help',
  SIGNALBRIDGE_DESTINATIONS:'/signalbridge/destinations',
  SIGNALBRIDGE_ROUTES:     '/signalbridge/routes',
  SIGNALBRIDGE_COLLECT:    '/signalbridge/collect',
  SIGNALBRIDGE_API_KEYS:   '/signalbridge/api-keys',
  SIGNALBRIDGE_ALERTS:     '/signalbridge/alerts',
  SIGNALBRIDGE_REPORTS:    '/signalbridge/reports',
  SIGNALBRIDGE_PURCHASE_REPORT: '/signalbridge/purchase-report',
  SIGNALBRIDGE_AUDIT:      '/signalbridge/audit',
  SIGNALBRIDGE_ONBOARDING: '/signalbridge/onboarding',
  SIGNALBRIDGE_SETTINGS:   '/signalbridge/settings',
  SIGNALBRIDGE_PIXEL_GROUPS:'/signalbridge/pixel-groups',
  SIGNALBRIDGE_GOOGLE:     '/signalbridge/google-ads',
  SIGNALBRIDGE_CONNECT_PIXEL: '/signalbridge/connect-pixel',
  SIGNALBRIDGE_CONNECT_STORE: '/signalbridge/connect-store',
  SIGNALBRIDGE_CONFIGURE_EVENTS: '/signalbridge/configure-events',
  SIGNALBRIDGE_MERGE_SETUP: '/signalbridge/merge-setup',
  SIGNALBRIDGE_MERGE:      '/signalbridge/merge',
  SIGNALBRIDGE_JOURNEY:    '/signalbridge/journey',
  SIGNALBRIDGE_DLQ:        '/signalbridge/dlq',
  SIGNALBRIDGE_COMPARE:    '/signalbridge/compare',

  // ── The PUBLIC fill link ────────────────────────────────────────────
  // Mounted OUTSIDE the authenticated shell (see App.jsx) because the
  // whole point of a published form is that a respondent who has no
  // Taskydo account can open it.
  //
  // Two spellings on purpose:
  //   * ``UNI_FORMS_PUBLIC_FILL``        — /uni-forms/f/:slug (canonical
  //     SPA route, matching every other route's hyphenated prefix);
  //   * ``UNI_FORMS_PUBLIC_FILL_LEGACY`` — /uni_forms/f/:slug (UNDERSCORE)
  //     which is what ``Form.share_url`` on the backend has always
  //     published, and therefore what is already sitting in respondents'
  //     inboxes.  Both render the same page; neither may be removed
  //     without breaking links that are already out in the world.
  UNI_FORMS_PUBLIC_FILL:        (slug = ':slug') => `/uni-forms/f/${slug}`,
  UNI_FORMS_PUBLIC_FILL_LEGACY: (slug = ':slug') => `/uni_forms/f/${slug}`,
  UNI_FORMS_SHARED_REPORT:      (token = ':token') => `/uni-forms/reports/shared/${token}`,
  UNI_FORMS_TOKEN_REPORT:       (token = ':token') => `/uni-forms/reports/t/${token}`,



  // Pharma SFA
  PHARMA_HOME:          '/pharma',
  PHARMA_WORKSPACE:     '/pharma/workspace',
  PHARMA_WORKSPACE_TILE:'/pharma/workspace/:tile',
  PHARMA_SFA_WEB:       '/pharma/sfa',
  PHARMA_SFA_WEB_SCREEN:'/pharma/sfa/:screen',
  PHARMA_DCR_LIST:      '/pharma/dcr',
  PHARMA_DCR_NEW:       '/pharma/dcr/new',
  PHARMA_DCR_DETAIL:    '/pharma/dcr/:id',
  PHARMA_TOUR_PROGRAMS: '/pharma/tour-programs',
  PHARMA_DAY_PLAN:      '/pharma/day-plan',
  PHARMA_DOCTORS:       '/pharma/doctors',
  PHARMA_CHEMISTS:      '/pharma/chemists',
  PHARMA_PRODUCTS:      '/pharma/products',
  PHARMA_POB:           '/pharma/pob',
  PHARMA_PRIMARY_SALES: '/pharma/primary-sales',
  PHARMA_PAYMENTS:      '/pharma/payments',
  PHARMA_STOCK:         '/pharma/stock',
  PHARMA_SECONDARY_SALES: '/pharma/secondary-sales',
  PHARMA_CHEMIST_SALES: '/pharma/chemist-sales',
  PHARMA_RCPA:          '/pharma/rcpa',
  PHARMA_EXPENSES:      '/pharma/expenses',
  PHARMA_TARGETS:       '/pharma/targets',
  PHARMA_DASHBOARD:     '/pharma/dashboard',
  PHARMA_E_DETAIL:      '/pharma/e-detailing',
  PHARMA_CHATBOT:       '/pharma/assistant',
  PHARMA_BIOMETRIC:     '/pharma/biometric',
  PHARMA_TERRITORY:     '/pharma/territory-alignment',
  // Admin-only SFA settings screen (level 1 of the three-level model).
  PHARMA_SFA_CONFIG:    '/pharma/configuration',
  // Stockists sit alongside doctors/chemists as an approvable field
  // master; the rest are the admin-side configuration masters.
  PHARMA_STOCKISTS:     '/pharma/stockists',
  PHARMA_APPROVALS:     '/pharma/approvals',
  PHARMA_M_HIERARCHY:   '/pharma/masters/hierarchy',
  PHARMA_M_TAX:         '/pharma/masters/tax',
  PHARMA_M_EXPENSE:     '/pharma/masters/expenses',
  PHARMA_M_HEADQUARTERS:'/pharma/masters/headquarters',
  PHARMA_M_TERRITORIES: '/pharma/masters/territories',
  PHARMA_M_TERRITORY_LEGS:'/pharma/masters/territory-legs',
  PHARMA_M_PRICE_LISTS: '/pharma/masters/price-lists',
  PHARMA_M_APPROVAL_RULES:'/pharma/masters/approvals',
  PHARMA_M_IMPORTS:     '/pharma/masters/uploads',

  // Super Admin
  SUPERADMIN_HOME:        '/super-admin',
  // Dedicated singletons (not CRUD resources). Must be registered in
  // App.jsx before SUPERADMIN_RESOURCE so slugs like ``login-security``
  // and ``db-viewer`` are not treated as generic ``:resource`` values.
  SUPERADMIN_LOGIN_SECURITY: '/super-admin/login-security',
  SUPERADMIN_DB_VIEWER: '/super-admin/db-viewer',
  SUPERADMIN_DB_VIEWER_ADD: '/super-admin/db-viewer/:alias/:schema/:table/add',
  SUPERADMIN_DB_VIEWER_TABLE: '/super-admin/db-viewer/:alias/:schema/:table',
  SUPERADMIN_DB_VIEWER_SCHEMA: '/super-admin/db-viewer/:alias/:schema',
  SUPERADMIN_MODULE_DEFAULTS: '/super-admin/module-defaults',
  SUPERADMIN_SUCCESSYATRA: '/super-admin/successyatra',
  SUPERADMIN_ORG_MODULES: '/super-admin/organisations/:id/modules',

  SUPERADMIN_RESOURCE:    '/super-admin/:resource',
  SUPERADMIN_RESOURCE_NEW:'/super-admin/:resource/new',
  SUPERADMIN_RESOURCE_EDIT:'/super-admin/:resource/:id/edit',
};

// Helpers to build URLs at runtime without typos.
export const buildPath = {
  tasksList:        (filter = 'my_tasks') => `/task/task-list/${filter}`,
  taskDetails:      (id) => `/task/task/${id}`,
  taskEdit:         (id) => `/task/edit-task/${id}`,
  routineTasks:     () => `/task/routine-tasks`,
  routineTaskNew:   () => `/task/routine-tasks/new`,
  routineTaskEdit:  (id) => `/task/routine-tasks/${id}/edit`,
  complaintsList:   (filter = 'my_complaints') => `/complaints/list/${filter}`,
  complaintDetails: (id) => `/complaints/details/${id}`,
  complaintEdit:    (id) => `/complaints/edit/${id}`,
  chatConversation: (id) => `/chat/${id}`,
};


// ---------------------------------------------------------------------------
// 4b. Public (unauthenticated) route prefixes
// ---------------------------------------------------------------------------
// Paths an anonymous visitor is *supposed* to be able to open.  Used by the
// axios 401 handler (``lib/api.js`` → ``redirectToLogin``) to decide whether
// losing the session should bounce the browser to ``/login``.
//
// Why this list has to exist
// --------------------------
// Every authenticated screen wants the bounce: a dead session there means
// nothing on the page will load, and silently showing an empty dashboard is
// worse than asking the user to sign in again.  But the app also serves
// pages whose whole purpose is to work WITHOUT an account — the marketing
// site and the Uni Forms public fill page (``/uni-forms/f/<slug>``, plus its
// legacy underscore twin).  On those, a 401 from an unrelated in-flight
// request — most often the ``AuthProvider`` bootstrap probe to ``/me/``,
// which 401s for every anonymous visitor by design — would replace the form
// a respondent just opened with a login screen they have no credentials for.
// That turns a published link into a dead end for precisely the audience it
// was shared with.
//
// Marketing/landing paths — public on the apex domain only.  Tenant
// subdomains redirect anonymous visitors to ``/login`` instead.
export const MARKETING_ROUTE_PREFIXES = [
  '/',
  '/features',
  '/solutions',
  '/help',
  '/privacy',
  '/copyright',
];

// Paths that stay public on EVERY host (apex and tenant subdomains).
// Keep the entries as PREFIXES (no params) so a slug / id in the path
// can't cause a miss.
export const PUBLIC_ROUTE_PREFIXES = [
  '/login',
  '/signup',
  '/password-recovery',
  '/access-denied',
  '/complaints/sso/login',
  // Uni Forms published share links — both spellings.  ``/uni_forms/``
  // (underscore) is what ``Form.share_url`` publishes server-side, so it
  // is the one already circulating; ``/uni-forms/f/`` is the canonical SPA
  // route.  Note the trailing ``f/`` on the hyphenated entry: without it
  // this would also match the authenticated builder screens under
  // ``/uni-forms/``, which DO want the login bounce.
  '/uni-forms/f/',
  '/uni_forms/',
];

function pathMatchesPrefix(path, prefix) {
  return path === prefix || (prefix !== '/' && path.startsWith(prefix));
}

/**
 * True when an anonymous visitor is allowed to stay on ``path`` without
 * being bounced to ``/login`` by the axios 401 handler.
 *
 * Marketing routes are apex-only; tenant subdomains always send visitors
 * to the login page instead.
 */
export function isPublicRoute(path, hostname) {
  const p = path || '';
  const host = (
    hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')
  ).toLowerCase();

  if (isPublicHost(host)) {
    if (MARKETING_ROUTE_PREFIXES.some((prefix) => pathMatchesPrefix(p, prefix))) {
      return true;
    }
  }

  return PUBLIC_ROUTE_PREFIXES.some((prefix) => pathMatchesPrefix(p, prefix));
}


// ---------------------------------------------------------------------------
// 5. Apps + side navigation
// ---------------------------------------------------------------------------
// Zoho-One style shell:
//   * Left rail   — one tile per APP.
//   * Top bar     — the current app's "pages" (children of the active app).
//
// `module`     — id used to highlight the active app.
// `urlPrefix`  — path prefix that determines whether THIS app is the active
//                one (the URL is the source of truth).
// `home`       — landing page when the icon in the left rail is clicked.
// `icon`       — string key resolved by ``AppIcon`` in ``components/Icons.jsx``
//                to the matching SVG component (the rail / top bar / mobile
//                menu render the SVG so it tints + scales cleanly across
//                operating systems instead of relying on platform emoji).
// `children`   — sub-pages shown in the top bar when this app is active.
// Each NAV entry carries an ``appKey`` that matches one of the strings
// returned by the backend in ``user.permitted_apps`` (see
// ``super_admin/api_views._compute_permitted_apps``).  The frontend uses
// it for two things:
//
//   1. The ``AppShell`` filters NAV entries down to the ones the user
//      may actually open, so menus reflect reality.
//   2. The ``RequirePermission`` route guard checks the same key before
//      rendering an app's pages, so deep-linking to a forbidden route
//      shows a friendly "no access" page instead of a 404 / API crash.
//
// Some apps (Tasks, Complaints, Chat) are tenant-only and ALSO require
// the user to be on a tenant subdomain.  ``tenantOnly: true`` records
// that — the AppShell will hide them on the public host even if the
// backend, for some reason, included the key in ``permitted_apps``.
//
// ``requireSuperAdmin`` and ``requireOrgAdmin`` are extra UX guards for
// admin-only shells; the *actual* enforcement is server-side.
//
// Per-child ``minAccess`` gate (module access-control feature)
// ------------------------------------------------------------
// A child link may carry ``minAccess`` — the minimum per-module access
// level (see ``ACCESS_LEVELS`` below) the user must have on the parent
// app for that specific link to render.  This powers the
// Business → Access Control feature: a user granted only "View only"
// on Tasks still sees the module tile and read-only pages, but the
// "Create Task" link (``minAccess: 'user'``) is hidden.  Links with no
// ``minAccess`` are visible to anyone who can open the module at all.
export const NAV = [

  {
    module: 'tasks',
    appKey: 'task',
    tenantOnly: true,
    label: 'Tasks',
    icon: 'tasks',
    urlPrefix: '/task',
    home: '/task',
    children: [
      { label: 'Dashboard',      to: '/task', screenKey: 'main.dashboard' },
      { label: 'My Tasks',       to: '/task/task-list/my_tasks', screenKey: 'main.my_tasks' },
      { label: 'My Team Tasks',  to: '/task/task-list/my_team_tasks', screenKey: 'main.my_team_tasks', individualHidden: true },
      { label: 'Tasks By Me',    to: '/task/task-list/my_created_tasks', screenKey: 'main.my_created_tasks' },
      { label: 'Create Task',    to: '/task/create-task', screenKey: 'main.create_task', minAction: 'create' },
      { label: 'Routine Tasks',  to: '/task/routine-tasks', screenKey: 'main.routine_tasks' },
    ],

  },
  {
    module: 'complaints',

    appKey: 'complaints',
    tenantOnly: true,
    label: 'Complaints',
    icon: 'complaints',
    urlPrefix: '/complaints',
    home: '/complaints',
    children: [
      { label: 'Dashboard',          to: '/complaints', screenKey: 'main.dashboard' },
      { label: 'My Complaints',      to: '/complaints/list/my_complaints', screenKey: 'main.my_complaints' },
      {
        label: 'My Team Complaints',
        to: '/complaints/list/my_team_complaints',
        screenKey: 'main.my_team_complaints',
        externalHidden: true,
        individualHidden: true,
      },
      { label: 'Create Complaint',   to: '/complaints/create', screenKey: 'main.create_complaint', minAction: 'create' },
    ],
  },

  {
    module: 'chat',
    appKey: 'chat',
    tenantOnly: true,
    label: 'Chat',
    icon: 'chat',
    urlPrefix: '/chat',
    home: '/chat',
    children: [
      { label: 'Inbox', to: '/chat', screenKey: 'main.inbox' },
    ],
  },
  {
    module: 'pharma',
    appKey: 'pharma',
    tenantOnly: true,
    label: 'Pharma SFA',
    icon: 'pharma',
    urlPrefix: '/pharma',
    home: '/pharma',
    // Pharma links carry a ``screenKey`` (matching a key in
    // ``PHARMA_SCREENS`` / the backend ``pharma_screens`` catalogue).
    // Unlike the coarse Admin/User/View/None gate every other module
    // uses, a Pharma link is shown IFF the user has ``read`` on that
    // specific screen — see ``visibleChildren`` + ``hasPharmaScreen``.
    children: [
      { label: 'My Workspace', to: '/pharma/workspace', screenKey: 'reports.my_dcrs', hint: 'Day plan, calls and reports' },
      { label: 'SFA Web', to: '/pharma/sfa', screenKey: 'reports.my_dcrs', hint: 'Menus, lists and reports for every role' },
      // Second-level menus.  A child with its own ``children`` renders
      // as a dropdown in the top bar (see ``ScrollableTabs``).  Each
      // nested link is still gated by ``screenKey`` — a group with no
      // visible items is dropped entirely.
      {
        label: 'Master',
        children: [
          {
            label: 'Headquarter Master',
            to: '/pharma/masters/headquarters',
            screenKey: 'masters.headquarters',
            hint: 'Posting towns for the field force',
          },
          {
            label: 'Doctor',
            to: '/pharma/doctors',
            screenKey: 'masters.doctors',
            hint: 'Added territory-wise',
          },
          {
            label: 'Chemist',
            to: '/pharma/chemists',
            screenKey: 'masters.chemists',
            hint: 'Added territory-wise',
          },
          {
            label: 'Stockist',
            to: '/pharma/stockists',
            screenKey: 'masters.stockists',
            hint: 'Added headquarter-wise',
          },
          {
            label: 'Products',
            to: '/pharma/products',
            screenKey: 'masters.products',
            hint: 'Added company-wise',
          },
          {
            label: 'Territory',
            to: '/pharma/territory-alignment',
            screenKey: 'tools.territory',
            hint: 'Sub-part of a headquarter',
          },
          {
            label: 'Target master',
            to: '/pharma/targets',
            screenKey: 'sales.targets',
          },
          {
            label: 'Hierarchy master',
            to: '/pharma/masters/hierarchy',
            screenKey: 'masters.hierarchy',
          },
          {
            label: 'Tax master',
            to: '/pharma/masters/tax',
            screenKey: 'masters.tax',
          },
          {
            label: 'Expense master',
            to: '/pharma/masters/expenses',
            screenKey: 'masters.expense_heads',
          },
          {
            label: 'Price List master',
            to: '/pharma/masters/price-lists',
            screenKey: 'masters.price_lists',
          },
          {
            label: 'Approval Master',
            to: '/pharma/masters/approvals',
            screenKey: 'masters.approval_rules',
          },
          {
            label: 'Territory Master',
            to: '/pharma/masters/territories',
            screenKey: 'masters.territories',
            hint: 'Territory records under a headquarter',
          },
          {
            label: 'Territory Legs',
            to: '/pharma/masters/territory-legs',
            screenKey: 'masters.territory_legs',
            hint: 'Agreed distance in KM',
          },
          {
            label: 'Approvals',
            to: '/pharma/approvals',
            screenKey: 'masters.approvals',
            minAction: 'update',
            hint: 'Queue of pending doctors, chemists and stockists',
          },
          {
            label: 'Upload History',
            to: '/pharma/masters/uploads',
            screenKey: 'masters.import_batches',
            hint: 'Excel imports of master data',
          },
          {
            label: 'SFA Configuration',
            to: '/pharma/configuration',
            screenKey: 'config.sfa_settings',
            minAction: 'update',
          },
        ],
      },
      {
        label: 'DCR',
        children: [
          {
            label: 'New DCR',
            to: '/pharma/dcr/new',
            screenKey: 'reports.my_dcrs',
            minAction: 'create',
          },
          {
            label: 'My DCR',
            to: '/pharma/dcr',
            screenKey: 'reports.my_dcrs',
          },
          {
            label: 'Tour program',
            to: '/pharma/tour-programs',
            screenKey: 'reports.tour_programs',
          },
          {
            label: 'Day plan',
            to: '/pharma/day-plan',
            screenKey: 'reports.day_plan',
          },
          {
            label: 'POB',
            to: '/pharma/pob',
            screenKey: 'sales.pob',
          },
          {
            label: 'Primary Sale',
            to: '/pharma/primary-sales',
            screenKey: 'sales.primary',
            hint: 'Stockist-wise company sale, web or Excel',
          },
          {
            label: 'Payments',
            to: '/pharma/payments',
            screenKey: 'sales.payments',
            hint: 'Money received from a party, web, Excel or CSV',
          },
          {
            label: 'Closing Stock',
            to: '/pharma/stock',
            screenKey: 'sales.stock',
            hint: 'Quantity still with the party',
          },
          {
            label: 'Secondary Sale',
            to: '/pharma/secondary-sales',
            screenKey: 'sales.secondary',
            hint: 'Primary Sale minus closing stock',
          },
          {
            label: 'Chemist Sales',
            to: '/pharma/chemist-sales',
            screenKey: 'sales.chemist',
            hint: 'Quantity times an editable price',
          },
          {
            label: 'RCPA',
            to: '/pharma/rcpa',
            screenKey: 'sales.rcpa',
          },
          {
            label: 'Expenses',
            to: '/pharma/expenses',
            screenKey: 'sales.expenses',
            hint: 'Claims from the field day',
          },
          {
            label: 'E-Detailing',
            to: '/pharma/e-detailing',
            screenKey: 'analytics.e_detailing',
            hint: 'Visual aids used on calls',
          },
          {
            label: 'Biometric',
            to: '/pharma/biometric',
            screenKey: 'tools.biometric',
            hint: 'Field attendance',
          },
        ],
      },
      { label: 'Dashboard',       to: '/pharma',                    screenKey: 'reports.dashboard' },
      { label: 'Manager Dashboard', to: '/pharma/dashboard',        screenKey: 'analytics.manager_dashboard' },
      { label: 'Assistant',       to: '/pharma/assistant',          screenKey: 'analytics.assistant' },
    ],
  },

  {
    module: 'uni-email',
    appKey: 'uni-email',
    tenantOnly: true,
    label: 'Email Marketing',
    icon: 'email',
    urlPrefix: '/uni-email',
    home: '/uni-email',
    children: [
      { label: 'Dashboard',  to: '/uni-email', screenKey: 'main.dashboard' },
      { label: 'Campaigns',  to: '/uni-email/campaigns', screenKey: 'main.campaigns' },
      { label: 'Contacts',   to: '/uni-email/contacts', screenKey: 'main.contacts' },
      { label: 'Lists',      to: '/uni-email/lists', screenKey: 'main.lists' },
      { label: 'Templates',  to: '/uni-email/templates', screenKey: 'main.templates' },
    ],
  },
  {
    module: 'uni-sms',
    appKey: 'uni-sms',
    tenantOnly: true,
    label: 'SMS / DLT',
    icon: 'sms',
    urlPrefix: '/uni-sms',
    home: '/uni-sms',
    children: [
      { label: 'Dashboard',     to: '/uni-sms', screenKey: 'main.dashboard' },
      { label: 'Campaigns',     to: '/uni-sms/campaigns', screenKey: 'main.campaigns' },
      { label: 'Contacts',      to: '/uni-sms/contacts', screenKey: 'main.contacts' },
      { label: 'Lists',         to: '/uni-sms/lists', screenKey: 'main.lists' },
      { label: 'DLT Templates', to: '/uni-sms/dlt-templates', screenKey: 'main.dlt_templates' },
    ],
  },
  {
    module: 'uni-forms',
    appKey: 'uni-forms',
    tenantOnly: true,
    label: 'Form Builder',
    icon: 'forms',
    urlPrefix: '/uni-forms',
    home: '/uni-forms',
    children: [
      { label: 'Dashboard', to: '/uni-forms', screenKey: 'main.dashboard' },
      { label: 'Forms',     to: '/uni-forms/forms', screenKey: 'main.forms' },
      { label: 'For Me',    to: '/uni-forms/for-me', screenKey: 'main.for_me' },
      { label: 'My Submissions', to: '/uni-forms/my-submissions', screenKey: 'main.for_me' },
      { label: 'Quizzes',   to: '/uni-forms/quizzes', screenKey: 'main.quizzes' },
      { label: 'Polls',     to: '/uni-forms/polls', screenKey: 'main.polls' },
      { label: 'Templates', to: '/uni-forms/templates', screenKey: 'main.templates' },
      { label: 'Audience',  to: '/uni-forms/audience', screenKey: 'main.audience', minAction: 'update' },
      { label: 'Figures',   to: '/uni-forms/figures', screenKey: 'main.figures', minAction: 'update' },
      { label: 'Reports',   to: '/uni-forms/reports', screenKey: 'main.reports' },
      { label: 'Report templates', to: '/uni-forms/report-templates', screenKey: 'main.report_templates' },
    ],

  },
  {
    module: 'waba-chat',
    appKey: 'waba-chat',
    tenantOnly: true,
    label: 'WhatsApp',
    icon: 'whatsapp',
    urlPrefix: '/waba-chat',
    home: '/waba-chat',
    children: [
      { label: 'Dashboard', to: '/waba-chat', screenKey: 'main.dashboard' },
      { label: 'Inbox', to: '/waba-chat/inbox', screenKey: 'main.inbox' },
      { label: 'Contacts', to: '/waba-chat/contacts', screenKey: 'main.contacts' },
      { label: 'Lists', to: '/waba-chat/lists', screenKey: 'main.lists' },
      { label: 'Broadcasts', to: '/waba-chat/broadcasts', screenKey: 'main.broadcasts' },
      { label: 'Templates', to: '/waba-chat/templates', screenKey: 'main.templates' },
      { label: 'Media', to: '/waba-chat/media', screenKey: 'main.media' },
      { label: 'Analytics', to: '/waba-chat/analytics', screenKey: 'main.analytics' },
      { label: 'Webhooks', to: '/waba-chat/webhooks', screenKey: 'main.webhooks' },
      { label: 'Incoming logs', to: '/waba-chat/incoming-logs', screenKey: 'main.incoming_logs' },
      { label: 'Settings', to: '/waba-chat/settings', screenKey: 'main.settings' },
      { label: 'Numbers', to: '/waba-chat/numbers', screenKey: 'main.numbers' },
      { label: 'S3 media', to: '/waba-chat/s3', screenKey: 'main.s3' },
      { label: 'Retry settings', to: '/waba-chat/retry', screenKey: 'main.retry' },
      { label: 'Throttle', to: '/waba-chat/throttle', screenKey: 'main.throttle' },
      { label: 'Account health', to: '/waba-chat/health', screenKey: 'main.health' },
      { label: 'Failed messages', to: '/waba-chat/failed', screenKey: 'main.failed' },
      { label: 'Automation', to: '/waba-chat/automation', screenKey: 'main.automation' },
      { label: 'Bot Studio', to: '/waba-chat/flows', screenKey: 'main.flows' },
      { label: 'API keys', to: '/waba-chat/api-keys', screenKey: 'main.api' },
      { label: 'Hooks', to: '/waba-chat/hooks', screenKey: 'main.hooks' },
      { label: 'Quick replies', to: '/waba-chat/quick-replies', screenKey: 'main.quick_replies' },
      { label: 'Auto replies', to: '/waba-chat/auto-replies', screenKey: 'main.auto_replies' },
      { label: 'Teams', to: '/waba-chat/teams', screenKey: 'main.teams' },
      { label: 'Form dialogs', to: '/waba-chat/form-dialogs', screenKey: 'main.form_dialogs' },
      { label: 'Form sessions', to: '/waba-chat/form-sessions', screenKey: 'main.form_sessions' },
      { label: 'KYC sessions', to: '/waba-chat/kyc', screenKey: 'main.kyc' },
      { label: 'Chat notes', to: '/waba-chat/notes', screenKey: 'main.notes' },
      { label: 'Tags', to: '/waba-chat/tags', screenKey: 'main.tags' },
      { label: 'Labels', to: '/waba-chat/labels', screenKey: 'main.labels' },
      { label: 'Help', to: '/waba-chat/help', screenKey: 'main.help' },
      { label: 'Features', to: '/waba-chat/features', screenKey: 'main.features' },
    ],
  },
  {
    module: 'rag',
    appKey: 'rag',
    tenantOnly: true,
    label: 'RAG',
    icon: 'rag',
    urlPrefix: '/rag',
    home: '/rag',
    children: [
      { label: 'Dashboard', to: '/rag', screenKey: 'main.dashboard' },
      { label: 'Knowledge', to: '/rag/knowledge', screenKey: 'main.knowledge' },
      { label: 'Versions', to: '/rag/versions', screenKey: 'main.versions' },
      { label: 'Chunks', to: '/rag/chunks', screenKey: 'main.chunks' },
      { label: 'Categories', to: '/rag/categories', screenKey: 'main.categories' },
      { label: 'Training', to: '/rag/training', screenKey: 'main.training' },
      { label: 'Quality', to: '/rag/quality', screenKey: 'main.quality' },
      { label: 'FAQs', to: '/rag/faq', screenKey: 'main.faq' },
      { label: 'Chat analytics', to: '/rag/chats', screenKey: 'main.chats' },
      { label: 'Conversations', to: '/rag/conversations', screenKey: 'main.conversations' },
      { label: 'Customers', to: '/rag/customers', screenKey: 'main.customers' },
      { label: 'Sales', to: '/rag/sales', screenKey: 'main.sales' },
      { label: 'Escalation', to: '/rag/escalation', screenKey: 'main.escalation' },
      { label: 'Follow-ups', to: '/rag/followups', screenKey: 'main.followups' },
      { label: 'Sequences', to: '/rag/sequences', screenKey: 'main.sequences' },
      { label: 'Sequence steps', to: '/rag/sequence-steps', screenKey: 'main.sequence_steps' },
      { label: 'Trackers', to: '/rag/trackers', screenKey: 'main.trackers' },
      { label: 'Reports', to: '/rag/reports', screenKey: 'main.reports' },
      { label: 'Health reports', to: '/rag/health-reports', screenKey: 'main.health_reports' },
      { label: 'Systems', to: '/rag/systems', screenKey: 'main.systems' },
      { label: 'Products', to: '/rag/products', screenKey: 'main.products' },
      { label: 'Playground', to: '/rag/playground', screenKey: 'main.playground' },
      { label: 'Test chat', to: '/rag/test-chat', screenKey: 'main.test_chat' },
      { label: 'Configuration', to: '/rag/config', screenKey: 'main.config' },
      { label: 'Advanced keys', to: '/rag/config/keys', screenKey: 'main.config_keys' },
      { label: 'Costs', to: '/rag/costs', screenKey: 'main.costs' },
      { label: 'Audit', to: '/rag/audit', screenKey: 'main.audit' },
      { label: 'Alerts', to: '/rag/alerts', screenKey: 'main.alerts' },
      { label: 'Compliance', to: '/rag/compliance', screenKey: 'main.compliance' },
      { label: 'Response templates', to: '/rag/response-templates', screenKey: 'main.templates' },
      { label: 'Document templates', to: '/rag/document-templates', screenKey: 'main.doc_templates' },
      { label: 'Hooks', to: '/rag/hooks', screenKey: 'main.hooks' },
      { label: 'Feedback', to: '/rag/feedback', screenKey: 'main.feedback' },
      { label: 'Notes', to: '/rag/notes', screenKey: 'main.notes' },
      { label: 'Automation', to: '/rag/automation', screenKey: 'main.automation' },
      { label: 'Shopify', to: '/rag/shopify', screenKey: 'main.shopify' },
      { label: 'Team', to: '/rag/team', screenKey: 'main.team' },
      { label: 'Help', to: '/rag/help', screenKey: 'main.help' },
      { label: 'Features', to: '/rag/features', screenKey: 'main.features' },
    ],
  },
  {
    module: 'signalbridge',
    appKey: 'signalbridge',
    tenantOnly: true,
    label: 'SignalBridge',
    icon: 'signalbridge',
    urlPrefix: '/signalbridge',
    home: '/signalbridge',
    children: [
      { label: 'Overview', to: '/signalbridge', screenKey: 'main.dashboard' },
      { label: 'Purchase report', to: '/signalbridge/purchase-report', screenKey: 'main.dashboard' },
      { label: 'Get started', to: '/signalbridge/onboarding', screenKey: 'main.onboarding' },
      { label: 'Stores', to: '/signalbridge/stores', screenKey: 'main.stores' },
      { label: 'Browser collect', to: '/signalbridge/collect', screenKey: 'main.collect' },
      { label: 'Datasets', to: '/signalbridge/pixels', screenKey: 'main.pixels' },
      { label: 'Events', to: '/signalbridge/events', screenKey: 'main.events' },
      { label: 'Templates', to: '/signalbridge/templates', screenKey: 'main.middleware' },
      { label: 'Payloads', to: '/signalbridge/payloads', screenKey: 'main.payloads' },
      { label: 'Reports', to: '/signalbridge/reports', screenKey: 'main.reports' },
      { label: 'Health', to: '/signalbridge/health', screenKey: 'main.health' },
      { label: 'Settings', to: '/signalbridge/settings', screenKey: 'main.settings' },
      { label: 'Help', to: '/signalbridge/help', screenKey: 'main.help' },
      { label: 'Add Pixel', to: '/signalbridge/connect-pixel', screenKey: 'main.connect_pixel', hideFromTabs: true },
      { label: 'Connect store', to: '/signalbridge/connect-store', screenKey: 'main.connect_store', hideFromTabs: true },
      { label: 'Configure events', to: '/signalbridge/configure-events', screenKey: 'main.configure_events', hideFromTabs: true },
      { label: 'Merge setup', to: '/signalbridge/merge-setup', screenKey: 'main.merge_setup', hideFromTabs: true },
      { label: 'Merge', to: '/signalbridge/merge', screenKey: 'main.merge', hideFromTabs: true },
      { label: 'Destinations', to: '/signalbridge/destinations', screenKey: 'main.destinations', hideFromTabs: true },
      { label: 'Pixel groups', to: '/signalbridge/pixel-groups', screenKey: 'main.pixel_groups', hideFromTabs: true },
      { label: 'Connect Google Ads', to: '/signalbridge/google-ads', screenKey: 'main.google_ads', hideFromTabs: true },
      { label: 'Event routes', to: '/signalbridge/routes', screenKey: 'main.routes', hideFromTabs: true },
      { label: 'Parameters', to: '/signalbridge/parameters', screenKey: 'main.parameters', hideFromTabs: true },
      { label: 'Send test event', to: '/signalbridge/test-event', screenKey: 'main.test_event', hideFromTabs: true },
      { label: 'Historical resend', to: '/signalbridge/resend', screenKey: 'main.resend', hideFromTabs: true },
      { label: 'Upload', to: '/signalbridge/upload', screenKey: 'main.upload', hideFromTabs: true },
      { label: 'Order journey', to: '/signalbridge/journey', screenKey: 'main.journey', hideFromTabs: true },
      { label: 'Dead letter queue', to: '/signalbridge/dlq', screenKey: 'main.dlq', hideFromTabs: true },
      { label: 'API keys', to: '/signalbridge/api-keys', screenKey: 'main.api_keys', hideFromTabs: true },
      { label: 'Alerts', to: '/signalbridge/alerts', screenKey: 'main.alerts', hideFromTabs: true },
      { label: 'Pixel comparison', to: '/signalbridge/compare', screenKey: 'main.pixel_compare', hideFromTabs: true },
      { label: 'Audit', to: '/signalbridge/audit', screenKey: 'main.audit', hideFromTabs: true },
    ],
  },
  {
    module: 'business',
    appKey: 'business',

    tenantOnly: true,
    requireOrgAdmin: true,
    label: 'Business',
    icon: 'business',
    urlPrefix: '/business',
    home: '/business',
    children: [
      { label: 'Overview',     to: '/business' },
      // In-app name and logo for the shared mobile client. The App
      // Store / Play listing stays taskydo; members see this after
      // they sign in. See ``BusinessAppBranding.jsx``.
      { label: 'App branding', to: '/business/app-branding' },
      // ``Users`` lives under the Business shell on tenant subdomains
      // so an org admin can manage their own org's users without
      // ever leaving the tenant — the route hits the same
      // ``/api/super_admin/users/`` viewset that the public super-
      // admin uses, but the tenant URLConf + JWT/binding middleware
      // chain hard-scopes the request to the caller's organisation.
      // See ``BUSINESS_RESOURCES.users`` for the form contract.
      { label: 'Users',        to: '/business/users' },
      { label: 'Divisions',    to: '/business/divisions' },
      // Second level of the complaint-routing tree.  Needed for the
      // ``sub_division`` picker on the Users form to have options.
      { label: 'Sub-Divisions',to: '/business/sub-divisions' },

      { label: 'Departments',  to: '/business/departments' },
      { label: 'Depots',       to: '/business/depots' },
      { label: 'Products',     to: '/business/products' },
      { label: 'Projects',     to: '/business/projects' },
      { label: 'Groups',       to: '/business/groups' },
      { label: 'HR',           to: '/business/hr-managements' },
      { label: 'Templates',    to: '/business/notice-templates' },
      { label: 'Public Admins',to: '/business/public-complaint-admins' },
      // ``Access Control`` — per-user, per-module access-level matrix
      // (Admin / User / View only / None).  Powers the module
      // access-control feature: an org admin picks, for each user and
      // each business module, how much access they get.  Backed by
      // ``/api/super_admin/module-access/`` (see ``MODULE_ACCESS_*``
      // endpoints above) and rendered by ``BusinessAccessControl.jsx``.
      { label: 'Access Control', to: '/business/access-control' },
      // ``Default`` is the system-defaults inspector / seeder.  It

      // surfaces (and idempotently provisions) the fixtures every
      // brand-new tenant needs — default Roles, default Permissions,
      // default Divisions and the Auretics SSO support
      // infrastructure (``support@auretics.com`` user, ``auretics-
      // users`` role, ``Complaints`` permission with full CRUD, the
      // role binding and the ``Auretics`` division).  Without these,
      // tenant signup, the @is_authorized gate, public-complaint
      // creation and the SSO flow all break in subtle ways — see
      // ``organisational_admin/api_defaults.py`` for the full
      // contract.
      { label: 'Default',      to: '/business/defaults' },
      { label: 'Google Sheets', to: '/business/integrations/google-sheets' },
      { label: 'Public Complaints', to: '/business/public-complaints' },
      { label: 'Complaint Settings', to: '/business/complaint-settings' },
      { label: 'Timezone', to: '/business/timezone' },
      { label: 'Backup', to: '/business/schema-backup' },
    ],

  },

  {
    module: 'super-admin',
    appKey: 'super-admin',
    requireSuperAdmin: true,
    label: 'Super Admin',
    icon: 'superAdmin',
    urlPrefix: '/super-admin',
    home: '/super-admin',
    children: [
      { label: 'Overview',       to: '/super-admin' },
      { label: 'Organisations',  to: '/super-admin/organisations' },
      { label: 'Users',          to: '/super-admin/users' },
      { label: 'Roles',          to: '/super-admin/roles' },
      { label: 'Permissions',    to: '/super-admin/permissions' },
      { label: 'Role-Perms',     to: '/super-admin/role-permissions' },
      { label: 'Hierarchies',    to: '/super-admin/hierarchies' },
      { label: 'Modules',        to: '/super-admin/modules' },
      { label: 'Default modules', to: '/super-admin/module-defaults' },
      { label: 'SuccessYatra move', to: '/super-admin/successyatra' },
      { label: 'Controls',       to: '/super-admin/controls' },
      { label: 'Database Viewer', to: '/super-admin/db-viewer' },
      { label: 'Bot Security',   to: '/super-admin/login-security' },
    ],
  },
];


// ---------------------------------------------------------------------------
// 6. Domain enums (mirror the Django model `choices` 1-to-1)
// ---------------------------------------------------------------------------
// Status colours mirror the original Taskose theme's `.task-status.type-N`
// classes (see static/task_static/css/styles.css around line 7257).
export const TASK_STATUS = [
  { value: 'assigned',             label: 'Assigned',          colour: '#ec4899' }, // pink   (type-1)
  { value: 'in_progress',          label: 'In Progress',       colour: '#34c8de' }, // cyan   (type-2)
  { value: 'waiting_for_feedback', label: 'Awaiting Feedback', colour: '#f9771d' }, // orange (type-3)
  { value: 'testing',              label: 'Testing',           colour: '#778899' }, // slate  (type-5)
  { value: 'completed',            label: 'Completed',         colour: '#5fd788' }, // green  (type-4 / --success)
];

export const TASK_URGENCY = [
  { value: 'default',   label: 'Default'   },
  { value: 'urgent',    label: 'Urgent'    },
  { value: 'imploring', label: 'Imploring' },
];

export const TASK_TEAM = [
  { value: 'individual', label: 'Individual' },
  { value: 'group',      label: 'Group' },
];

// Routine-task domain enums.  Mirror the ``RoutineTask`` model
// choices in ``task_management/models.py`` so the form's <select>
// options stay in lock-step with the backend validators.
export const ROUTINE_FREQUENCIES = [
  { value: 'daily',    label: 'Every day' },
  { value: 'weekly',   label: 'Every week (pick a weekday)' },
  { value: 'monthly',  label: 'Every month (pick a date)' },
  { value: 'annually', label: 'Every year (pick a date and month)' },
];

export const ROUTINE_WEEKDAYS = [
  { value: 'Monday',    label: 'Monday' },
  { value: 'Tuesday',   label: 'Tuesday' },
  { value: 'Wednesday', label: 'Wednesday' },
  { value: 'Thursday',  label: 'Thursday' },
  { value: 'Friday',    label: 'Friday' },
  { value: 'Saturday',  label: 'Saturday' },
  { value: 'Sunday',    label: 'Sunday' },
];

export const ROUTINE_MONTHS = [
  { value: '1',  label: 'January' },
  { value: '2',  label: 'February' },
  { value: '3',  label: 'March' },
  { value: '4',  label: 'April' },
  { value: '5',  label: 'May' },
  { value: '6',  label: 'June' },
  { value: '7',  label: 'July' },
  { value: '8',  label: 'August' },
  { value: '9',  label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

export const ROUTINE_STATUS = [
  { value: 'not_started', label: 'Not Started', colour: '#9ca3af' },
  { value: 'in_progress', label: 'In Progress', colour: '#34c8de' },
  { value: 'completed',   label: 'Completed',   colour: '#5fd788' },
];


export const COMPLAINT_STATUS = [
  { value: 'pending',     label: 'Pending',     colour: '#fcdb66' }, // warning
  { value: 'in_progress', label: 'In Progress', colour: '#34c8de' }, // info / cyan
  { value: 'resolved',    label: 'Resolved',    colour: '#5fd788' }, // success
  { value: 'drop',        label: 'Dropped',     colour: '#f05a5a' }, // danger
  { value: 'closed',      label: 'Closed',      colour: '#7980ff' }, // primary
];

// ---------------------------------------------------------------------------
// 7. Filters used by the list pages
// ---------------------------------------------------------------------------
export const TASK_FILTERS = [
  { value: 'my_tasks',         label: 'My Tasks' },
  { value: 'my_team_tasks',    label: 'My Team Tasks' },
  // "Tasks By Me" — tasks created by the current user (regardless of
  // who they're assigned to).  Wired to ``created_by_me=1`` on the
  // backend ``TaskViewSet`` and used by ``TasksList.jsx``.
  { value: 'my_created_tasks', label: 'Tasks By Me' },
];


export const COMPLAINT_FILTERS = [
  { value: 'my_complaints',         label: 'My Complaints' },
  { value: 'my_team_complaints',    label: 'My Team Complaints' },
  { value: 'my_created_complaints', label: 'Created By Me' },
];

// ---------------------------------------------------------------------------
// 8. Pagination & misc
// ---------------------------------------------------------------------------
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_PARAM:   'page_size',
  PAGE_PARAM:        'page',
};

// Responsive breakpoints (also referenced from CSS — keep in sync there).
export const BREAKPOINTS = {
  mobile: 600,
  tablet: 1024,
};

// Chat reconnect backoff is owned by ``src/lib/chatSocket.js`` (WebSockets).
// POLL_INTERVAL_MS is unused by the live thread; kept so older callers
// that still import it do not crash.
export const CHAT = {
  POLL_INTERVAL_MS: 10_000,
};

export const WABA = {
  POLL_INTERVAL_MS: 10_000,
};

// Notification-bell polling interval — how often the top-bar inbox
// re-fetches ``GET /api/notification/inbox/``.  Independent of chat,
// which is now a WebSocket.
export const NOTIFICATIONS = {
  POLL_INTERVAL_MS: 10_000,
};

// Convenience helper used everywhere we need to colour a status badge.
export function statusMeta(domain, value) {
  const list = domain === 'task' ? TASK_STATUS : COMPLAINT_STATUS;
  return list.find((s) => s.value === value) || { value, label: value, colour: '#9ca3af' };
}

// Find the active app entry in NAV from a URL pathname.  Falls back to the
// first NAV entry if nothing matches (e.g. on the bare "/").
function shellNav() {
  const configured = getConfig();
  const nav = configured?.NAV || NAV;
  const key = configured?.APP?.productKey || APP.productKey || 'taskydo';
  return navForProduct(nav, key);
}

function navEntryMatches(entry, pathname) {
  if (!entry || !pathname) return false;
  const to = entry.to;
  if (to && to !== '/' && (pathname === to || pathname.startsWith(`${to}/`))) return true;
  if (to === '/' && pathname === '/') return true;
  return (entry.children || []).some((child) => navEntryMatches(child, pathname));
}

export function activeApp(pathname) {
  const list = shellNav();
  if (!pathname || !list.length) return list[0];
  for (const app of list) {
    if (app.urlPrefix && (pathname === app.urlPrefix || pathname.startsWith(`${app.urlPrefix}/`))) {
      return app;
    }
    if (navEntryMatches(app, pathname)) return app;
  }
  return list[0];
}

// ---------------------------------------------------------------------------
// 9. Permission helpers
// ---------------------------------------------------------------------------
// Look up a NAV entry by its ``appKey`` (the same identifier the
// backend returns in ``user.permitted_apps``).
export function findNavByAppKey(appKey) {
  if (!appKey) return null;
  return shellNav().find((n) => n.appKey === appKey) || null;
}

/**
 * Decide whether the SPA may render the app described by ``nav`` for
 * the given ``user`` and current ``hostname``.
 *
 * Rules (in order):
 *   * Anonymous user → false.
 *   * NAV entry's ``requireSuperAdmin`` not satisfied → false.
 *   * On the public schema (per backend ``is_public_schema``):
 *       - Super-admins see only ``super-admin`` (platform-operator shell).
 *       - Personal / individual accounts see Tasks and Complaints only.
 *       - Nobody else gets tenant apps on this host.
 *   * On a tenant schema:
 *       - ``requireOrgAdmin`` apps need ``user.is_admin`` (or super).
 *       - Super-admins see every tenant app (impersonation support).
 *       - Org admins implicitly see every tenant app — mirrors the
 *         backend ``_compute_permitted_apps`` rule so a fresh tenant
 *         with no Roles / Permissions still works for its admin.
 *       - Other org users: app must be in ``user.permitted_apps``.
 *
 * Note: this is UX gating only — the backend still enforces every
 * permission on every request.
 */
export const INDIVIDUAL_APP_KEYS = ['task', 'complaints'];

/** Screens that imply a team / other people — hidden for solo accounts. */
export const INDIVIDUAL_HIDDEN_SCREENS = {
  task: ['main.my_team_tasks'],
  complaints: ['main.my_team_complaints'],
};

/**
 * Personal account on the apex / public schema (no organisation).
 *
 * Prefers the backend ``is_individual`` flag.  Super-admins on the same
 * host are platform operators, not individuals.
 */
export function isIndividualUser(user) {
  if (!user) return false;
  if (user.is_super_admin || user.is_superuser) return false;
  if (typeof user.is_individual === 'boolean') return !!user.is_individual;
  const onPublic = (typeof user.is_public_schema === 'boolean')
    ? user.is_public_schema
    : isPublicHost();
  return onPublic;
}

/**
 * True when the current session is a platform super-admin on the
 * public / apex host.  That operator may open Super Admin *and* the
 * public organisation's Business dashboard.
 */
export function isSuperOnPublicSchema(user, hostname) {
  if (!user || !(user.is_super_admin || user.is_superuser)) return false;
  if (typeof user.is_public_schema === 'boolean') return user.is_public_schema;
  return isPublicHost(hostname);
}


export function canAccessApp(nav, user, hostname) {

  if (!nav || !user) return false;
  const isSuper = !!user.is_super_admin || !!user.is_superuser;
  const isOrgAdmin = !!user.is_admin;

  // Trust the backend's authoritative schema flag when present.  The
  // ``/me/`` payload includes ``is_public_schema`` which is computed
  // from ``django.db.connection.schema_name`` — i.e. the real schema
  // django-tenants resolved from the request's Host header.  Falling
  // back to the local ``isPublicHost(hostname)`` heuristic only when
  // the backend didn't send the flag (legacy sessions).
  //
  // Why this matters: ``isPublicHost`` depends on
  // ``VITE_BASE_DOMAIN`` matching the deployment's actual apex
  // domain.  When that env var is mis-configured or left at the
  // ``localhost`` default in production, every tenant subdomain
  // (e.g. ``acme.tasky.do``) is wrongly classified as "public",
  // ``permitted_apps`` is filtered down to only ``super-admin`` and
  // an org admin landing on their tenant gets a blank rail with the
  // "Access denied" page.  Using the backend flag eliminates that
  // foot-gun — the server is the single source of truth.
  const onPublic = (typeof user.is_public_schema === 'boolean')
    ? user.is_public_schema
    : isPublicHost(hostname);

  if (nav.requireSuperAdmin && !isSuper) return false;

  if (onPublic) {
    if (isSuper) {
      // Operator shell + public-organisation business dashboard.
      return nav.appKey === 'super-admin' || nav.appKey === 'business';
    }
    const apps = Array.isArray(user.permitted_apps) && user.permitted_apps.length
      ? user.permitted_apps
      : INDIVIDUAL_APP_KEYS;
    return apps.includes(nav.appKey);
  }

  // ---- Tenant host below ------------------------------------------
  if (nav.requireOrgAdmin && !(isSuper || isOrgAdmin)) return false;

  // Super-admin on a tenant subdomain: allow every tenant app
  // (impersonation / inspection support).
  if (isSuper) return true;

  // Org admins see the business shell plus every module super-admin
  // has activated for this organisation (``permitted_apps``).
  if (isOrgAdmin) {
    if (nav.appKey === 'business') return true;
    const apps = Array.isArray(user.permitted_apps) ? user.permitted_apps : [];
    if (!apps.length) return true;
    return apps.includes(nav.appKey);
  }

  // Org user: must be in permitted_apps.
  const apps = Array.isArray(user.permitted_apps) ? user.permitted_apps : [];
  return apps.includes(nav.appKey);
}


/**
 * Pick the first NAV entry the user is allowed to land on after login.
 * Used by the ``/`` and ``/login`` redirect logic.  Returns ``null``
 * when the user has no permitted apps (the SPA then renders a friendly
 * "no access" page instead of a 404).
 *
 * The "am I on the public host?" decision uses the backend-supplied
 * ``user.is_public_schema`` flag when available, then falls back to
 * ``isPublicHost(hostname)``.  See ``canAccessApp`` for the rationale —
 * the same VITE_BASE_DOMAIN mis-configuration that broke the
 * permission gate would otherwise also send org admins to
 * ``/super-admin`` on login.
 */
export function firstPermittedApp(user, hostname) {
  if (!user) return null;
  const onPublic = (typeof user.is_public_schema === 'boolean')
    ? user.is_public_schema
    : isPublicHost(hostname);
  // Super-admin on the public host always lands on /super-admin.
  if ((user.is_super_admin || user.is_superuser) && onPublic) {
    return findNavByAppKey('super-admin');
  }
  for (const nav of shellNav()) {
    if (canAccessApp(nav, user, hostname)) return nav;
  }
  return null;
}

/** Reject open-redirect payloads; allow in-app paths including search. */
export function safeInternalNext(value) {
  const raw = String(value || '').trim();
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('://')) {
    return '';
  }
  // A ``next=/login`` (or /login?…) would bounce the post-login redirect
  // straight back onto the login page and can close a /login ↔ /task loop.
  const path = raw.split('?')[0];
  if (path === ROUTES.LOGIN || path.startsWith(`${ROUTES.LOGIN}/`)) {
    return '';
  }
  return raw;
}

export function pickPostLoginRoute(user, location) {
  const params = new URLSearchParams((location && location.search) || '');
  const fromQuery = safeInternalNext(params.get('next'));
  if (fromQuery) return fromQuery;
  const from = location && location.state && location.state.from;
  if (from && from.pathname && from.pathname !== ROUTES.LOGIN) {
    return `${from.pathname}${from.search || ''}`;
  }
  const target = firstPermittedApp(user);
  if (target) return target.home;
  return ROUTES.ROOT;
}


// ---------------------------------------------------------------------------
// 10. Per-module access levels (Business → Access Control feature)
// ---------------------------------------------------------------------------
// Mirrors ``super_admin.models.UserModuleAccess.ACCESS_LEVEL_CHOICES``
// 1-to-1.  These are the four access levels an org admin can grant a
// user on any single business module:
//
//   * ``none``  — no access; the module link is hidden entirely.
//   * ``view``  — read-only; the module opens but write actions
//                 (Create/Edit) are hidden.
//   * ``user``  — normal member; can create/edit their own records.
//   * ``admin`` — full module admin; every link visible.
//
// The ``rank`` is the numeric weight used by ``hasModuleAccess`` to
// compare "does the user meet the ``minAccess`` a link requires?".
// Keep the values (strings) identical to the backend choices — they
// travel over the wire in the ``/me/`` ``module_access`` map and the
// Access Control matrix API.
export const ACCESS_LEVELS = [
  { value: 'none',  label: 'None',      rank: 0, hint: 'No access — module hidden' },
  { value: 'view',  label: 'View only', rank: 1, hint: 'Read-only — cannot create or edit' },
  { value: 'user',  label: 'User',      rank: 2, hint: 'Can create and edit own records' },
  { value: 'admin', label: 'Admin',     rank: 3, hint: 'Full access to the module' },
];

// Fast value → rank lookup built once from ACCESS_LEVELS.
const ACCESS_RANK = ACCESS_LEVELS.reduce((acc, l) => {
  acc[l.value] = l.rank;
  return acc;
}, {});

/**
 * Resolve the effective access LEVEL string (``'admin'`` | ``'user'`` |
 * ``'view'`` | ``'none'``) the given ``user`` has on the module
 * identified by ``appKey``.
 *
 * Resolution order (mirrors the backend ``_compute_permitted_apps`` /
 * ``_module_access_map`` rules so the menu and the server agree):
 *   1. Super-admins and org admins implicitly have ``'admin'`` on every
 *      module — they're the accounts that GRANT access, so they always
 *      see every link.  An account that is ALSO flagged external is
 *      treated as external (fail closed) rather than given the admin
 *      menu.
 *   2. An explicit entry in ``user.module_access`` (the per-user,
 *      per-module map the backend puts in the ``/me/`` payload) wins.
 *      This includes extra modules an org admin later granted to an
 *      Auretics-portal user on Business → Access Control.
 *   3. External users with no explicit row default to ``'user'`` on
 *      complaints and ``'none'`` everywhere else.
 *   4. Otherwise fall back to ``'user'`` when the app is in
 *      ``permitted_apps`` (legacy permission-grid users keep their
 *      previous full-link behaviour), else ``'none'``.
 *
 * UX gating only — the backend still enforces the real permission on
 * every request.
 */
/**
 * Is this an Auretics **external complaint user**?
 *
 * These accounts arrive through the SSO handoff described in
 * ``docs/sso.md`` (``/complaints/sso/login/?token=…``).  By default they
 * may file a complaint and read the ones they filed.  An org admin can
 * later grant extra modules on Business → Access Control; those grants
 * must survive the next special-link login.
 *
 * The flag is computed SERVER-side (``_me_payload`` →
 * ``complaints_management.external_users.is_external_user``) and shipped
 * in the ``/me/`` payload, which is also what both SSO endpoints return.
 * We read the flag rather than sniffing ``user.role`` in JS so the
 * frontend and backend can never disagree about who is external — the
 * role name lives in exactly one place (Python).
 *
 * Reminder: this is UX gating ONLY.  ``ComplaintViewSet`` narrows the
 * queryset and refuses external writes regardless of what the SPA
 * renders.  Hiding a button here is a courtesy, not a security control.
 */
export function isExternalUser(user) {
  return !!(user && user.is_external_user);
}

/**
 * Capability map for the Complaints module, so the pages don't each
 * re-derive "should I show this?" from raw flags and drift apart.
 *
 * External users get exactly two verbs — create, and view their own.
 * Everything else in the complaint lifecycle (status transitions,
 * reassignment, edit, delete, resolution notes, browsing the team's
 * queue) belongs to the support team and is refused by the backend for
 * these accounts, so rendering the control would only produce a 403 the
 * user can't act on.
 */
export function complaintCapabilities(user) {
  const external = isExternalUser(user);
  const individual = isIndividualUser(user);
  const solo = external || individual;
  return {
    isExternal: external,
    isIndividual: individual,
    canCreate: true,
    // Write verbs — all denied for external users by
    // ``ComplaintViewSet._deny_external_write``.  Individuals may
    // still edit / close their own tickets; they just cannot hand
    // them to someone else.
    canEdit: !external,
    canDelete: !external,
    canChangeStatus: !external,
    canReassign: !solo,
    // Read-scope: can this user see anything beyond their own tickets?
    canSeeTeam: !solo,
    // The reassign picker needs the tenant user directory, which an
    // external or individual user has no business enumerating.
    canListUsers: !solo,
  };
}

export function moduleAccessLevel(user, appKey) {
  if (!user || !appKey) return 'none';
  const isAdmin = !!(user.is_super_admin || user.is_superuser || user.is_admin);
  // Mis-provisioned admin+external account: fail CLOSED rather than
  // handing it the admin menu.  ``is_external_user`` is already
  // mutually exclusive with admin/superuser server-side.
  if (isExternalUser(user) && isAdmin) {
    return appKey === 'complaints' ? 'user' : 'none';
  }
  if (isAdmin) return 'admin';

  const map = (user.module_access && typeof user.module_access === 'object')
    ? user.module_access
    : {};
  const explicit = map[appKey];
  if (explicit && Object.prototype.hasOwnProperty.call(ACCESS_RANK, explicit)) {
    return explicit;
  }

  // No explicit Access Control row: keep the complaints-only default
  // for portal users, otherwise fall back to the legacy permission grid.
  if (isExternalUser(user)) {
    return appKey === 'complaints' ? 'user' : 'none';
  }

  const apps = Array.isArray(user.permitted_apps) ? user.permitted_apps : [];
  return apps.includes(appKey) ? 'user' : 'none';
}

/**
 * True when ``user`` has AT LEAST ``required`` access on module
 * ``appKey``.  ``required`` defaults to ``'view'`` (i.e. "can open the
 * module at all").  Used by the AppShell to decide whether to render a
 * child link that carries a ``minAccess`` gate.
 */
export function hasModuleAccess(user, appKey, required = 'view') {
  const have = ACCESS_RANK[moduleAccessLevel(user, appKey)] ?? 0;
  const need = ACCESS_RANK[required] ?? 0;
  return have >= need;
}

/**
 * True when ``user`` has AT LEAST ``required`` action on the given
 * screen within ``appKey``.
 *
 * Per-screen CRUD access is configured via Business → Access Control.
 * The backend puts a ``screen_access`` map in the ``/me/`` payload::
 *
 *     { "task": { "main.my_tasks": {"create": true, "read": true, ...} },
 *       "pharma": { "reports.my_dcrs": {...}, ... }, ... }
 *
 * Pharma also exposes the legacy ``pharma_access`` flat map for backward
 * compatibility.  When a user has NO explicit screen rows for a module,
 * callers fall back to the coarse ``module_access`` level (Admin/User/
 * View/None) so existing tenants keep working.
 */
export function hasModuleScreen(user, appKey, screenKey, required = 'read') {
  if (!user || !appKey || !screenKey) return false;
  if (isIndividualUser(user)) {
    const hidden = INDIVIDUAL_HIDDEN_SCREENS[appKey] || [];
    if (hidden.includes(screenKey)) return false;
  }
  if (user.is_super_admin || user.is_superuser || user.is_admin) return true;

  const screenAccess = (user.screen_access && typeof user.screen_access === 'object')
    ? user.screen_access
    : {};
  let moduleGrid = screenAccess[appKey];
  if ((!moduleGrid || !Object.keys(moduleGrid).length) && appKey === 'pharma') {
    const legacy = (user.pharma_access && typeof user.pharma_access === 'object')
      ? user.pharma_access
      : {};
    if (Object.keys(legacy).length) moduleGrid = legacy;
  }
  if (moduleGrid && typeof moduleGrid === 'object' && Object.keys(moduleGrid).length) {
    const grid = moduleGrid[screenKey];
    if (!grid || typeof grid !== 'object') return false;
    return !!grid[required];
  }

  // No explicit screen grid — fall back to coarse module level.
  if (required === 'read') {
    return hasModuleAccess(user, appKey, 'view');
  }
  if (required === 'create' || required === 'update' || required === 'delete') {
    return hasModuleAccess(user, appKey, 'user');
  }
  return hasModuleAccess(user, appKey, 'view');
}

/**
 * @deprecated Use ``hasModuleScreen(user, 'pharma', screenKey, required)``.
 */
export function hasPharmaScreen(user, screenKey, required = 'read') {
  return hasModuleScreen(user, 'pharma', screenKey, required);
}

/**
 * Filter an app's ``children`` array down to the links the given
 * ``user`` may actually see, honouring each child's optional access
 * gate.  Two gate styles are supported:
 *
 *   * ``screenKey`` — the link shows IFF the user has the ``minAction``
 *     (default ``'read'``) on that specific screen within the parent app.
 *   * ``minAccess`` — legacy coarse Admin/User/View/None level (used when
 *     a link has no ``screenKey``).
 *
 * Children with neither gate are always kept (they only require the
 * module itself to be open).
 *
 * A child may itself have ``children``.  That is a second-level menu
 * (the Pharma "Master" dropdown).  Each nested link is filtered with
 * the same gates; the group is omitted when nothing inside it is
 * visible.  The original ``NAV`` object is never mutated.
 */
function childIsVisible(child, nav, user) {
  // ``externalHidden`` — link is meaningless for an Auretics portal
  // user because the backend scopes them to their own rows (e.g. a
  // "My Team" queue would always render empty).
  if (child.externalHidden && isExternalUser(user)) return false;
  if (child.individualHidden && isIndividualUser(user)) return false;
  // Keep the route + screenKey for access control, but omit it from
  // the top-bar tabs (used by SignalBridge so everyday users see a
  // short WeTracked-style nav instead of every power-user screen).
  if (child.hideFromTabs) return false;
  if (child.screenKey) {
    return hasModuleScreen(user, nav.appKey, child.screenKey, child.minAction || 'read');
  }
  if (!child.minAccess) return true;
  return hasModuleAccess(user, nav.appKey, child.minAccess);
}

export function visibleChildren(nav, user) {
  if (!nav || !Array.isArray(nav.children)) return [];
  const visible = [];
  for (const child of nav.children) {
    if (Array.isArray(child.children) && child.children.length) {
      const nested = child.children.filter((item) => childIsVisible(item, nav, user));
      if (!nested.length) continue;
      visible.push({ ...child, children: nested });
      continue;
    }
    if (childIsVisible(child, nav, user)) visible.push(child);
  }
  return visible;
}



export default {
  APP, API, HOST, STORAGE_KEYS, ROUTES, buildPath, NAV,
  TASK_STATUS, TASK_URGENCY, TASK_TEAM,
  COMPLAINT_STATUS, TASK_FILTERS, COMPLAINT_FILTERS,
  PAGINATION, BREAKPOINTS, CHAT, NOTIFICATIONS,
  ACCESS_LEVELS,
  statusMeta, activeApp,
  isPublicHost, tenantSlugFromHost, resolveApexDomain, isPublicRoute,
  sanitiseOrgCode, workspaceHostUrl,
  MARKETING_ROUTE_PREFIXES, PUBLIC_ROUTE_PREFIXES,
  canAccessApp, firstPermittedApp, findNavByAppKey,
  isSuperOnPublicSchema,
  pickPostLoginRoute, safeInternalNext,
  isIndividualUser, INDIVIDUAL_APP_KEYS,
  moduleAccessLevel, hasModuleAccess, visibleChildren,
  hasModuleScreen, hasPharmaScreen,
  isExternalUser, complaintCapabilities,
};




